import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { downloadFile, apiErr } from "@/lib/api";
import { LayoutDashboard, Users, Package, CreditCard, MessageCircle, LogOut, ShieldCheck, RefreshCw, Building2, BadgePercent, FileText, UserCog, UserPlus, TrendingUp, Wallet, ScanLine, Activity, Award, Download, Loader2 } from "lucide-react";
import AdminPayouts from "@/pages/AdminPayouts";
import AdminVerify from "@/pages/AdminVerify";
import AdminCheckin from "@/pages/AdminCheckin";
import AdminSettings from "@/pages/AdminSettings";
import AdminLogs from "@/pages/AdminLogs";
import AdminWhatsAppLogs from "@/pages/AdminWhatsAppLogs";
import AdminBenefits from "@/pages/AdminBenefits";
import AdminVisits from "@/pages/AdminVisits";
export { AdminPayouts, AdminVerify, AdminCheckin, AdminSettings, AdminLogs, AdminWhatsAppLogs, AdminBenefits, AdminVisits };
import MembershipCardModal from "@/components/MembershipCardModal";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import DateFilter from "@/components/DateFilter";
import Pagination from "@/components/Pagination";
import { inr } from "@/lib/format";
import { canAccess, hasCap } from "@/lib/permissions";
import { adminUrl, getAdminBase } from "@/lib/adminPath";
import { RelationSelect, validateFamilyComposition } from "@/lib/relations";
import { StateSelect, GSTIN_REGEX, stateFromGSTIN } from "@/lib/states";
import { ageLabel } from "@/lib/age";
import { MemberVisitsPanel, MemberBenefitsPanel } from "@/components/MemberEngagementPanels";
import { EngagementBadge } from "@/components/EngagementBadge";
import EngagementAnalytics from "@/components/EngagementAnalytics";

export function AdminLayout() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const [maintenance, setMaintenance] = useState(false);
  const [engagementOn, setEngagementOn] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !["super_admin", "admin", "accounts", "staff"].includes(user.role))) nav(adminUrl("/login"));
  }, [user, loading, nav]);

  useEffect(() => {
    let alive = true;
    const fetch = () => api.get("/maintenance-status")
      .then((r) => {
        if (!alive) return;
        setMaintenance(!!r.data.maintenance_mode);
        setEngagementOn(!!r.data.engagement_module_enabled);
      })
      .catch(() => {});
    fetch();
    const t = setInterval(fetch, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const adminBase = getAdminBase();
  const allLinks = [
    { to: adminBase,                          key: "dashboard",             label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: adminUrl("/leads"),                 key: "leads",                 label: "Leads CRM", icon: MessageCircle },
    { to: adminUrl("/plans"),                 key: "plans",                 label: "Plans", icon: Package },
    ...(engagementOn ? [{ to: adminUrl("/plan-benefits"), key: "plan-benefits", label: "Plan Benefits", icon: Award }] : []),
    { to: adminUrl("/members"),               key: "members",               label: "Members", icon: Users },
    ...(engagementOn ? [{ to: adminUrl("/visits"), key: "visits", label: "Visits", icon: Activity }] : []),
    { to: adminUrl("/renewals"),              key: "renewals",              label: "Renewals", icon: RefreshCw },
    { to: adminUrl("/payments"),              key: "payments",              label: "Payments", icon: CreditCard },
    { to: adminUrl("/corporate"),             key: "corporate",             label: "Corporate", icon: Building2 },
    { to: adminUrl("/employees"),             key: "employees",             label: "Employees", icon: UserCog },
    { to: adminUrl("/discount-codes"),        key: "referrals",             label: "Discount Codes", icon: BadgePercent },
    { to: adminUrl("/employee-performance"),  key: "employee-performance",  label: "Employee Report", icon: TrendingUp },
    { to: adminUrl("/payouts"),               key: "payouts",               label: "Payouts", icon: Wallet },
    { to: adminUrl("/verify"),                key: "verify",                label: "Verify Member", icon: ScanLine },
    ...(engagementOn ? [{ to: adminUrl("/checkin"), key: "verify", label: "QR Check-In", icon: ScanLine }] : []),
    { to: adminUrl("/invoices"),              key: "invoices",              label: "GST · Invoices", icon: FileText },
    { to: adminUrl("/logs"),                  key: "admin-logs",            label: "Activity Logs", icon: ShieldCheck },
    { to: adminUrl("/whatsapp-logs"),         key: "admin-logs",            label: "WhatsApp Logs", icon: MessageCircle },
    { to: adminUrl("/settings"),              key: "settings",              label: "Settings", icon: UserCog },
    { to: adminUrl("/users"),                 key: "users",                 label: "User Management", icon: UserPlus },
    { to: adminUrl("/roles"),                 key: "roles",                 label: "Roles & Permissions", icon: ShieldCheck },
  ];
  const links = allLinks.filter((l) => canAccess(l.key, user));

  return (
    <div className="min-h-screen flex bg-[#FFFFFF]">
      <aside className="w-64 border-r border-[#E1E8F0] bg-white flex flex-col sticky top-0 h-screen" data-testid="admin-sidebar">
        <Link to="/" className="p-6 flex items-center gap-2 border-b border-[#E1E8F0]">
          <img src="https://npc.nirmalhospitals.in/static/media/NIRMAL-HOSPITALS-LOGO-Final.362863c2d4d04a614b95.png" alt="Nirmal Hospital" className="h-9 w-auto" />
          <span className="font-display text-sm font-bold text-[#0F2540] border-l border-[#E1E8F0] pl-2">Admin</span>
        </Link>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-[#1E4F87] text-[#FFFFFF]" : "text-[#0F2540] hover:bg-[#EAF1FA]"
                }`}
              data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <button onClick={async () => { await logout(); nav("/"); }}
          className="m-4 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#E1E8F0] hover:border-[#1E4F87]"
          data-testid="admin-logout-btn">
          <LogOut className="h-4 w-4" /> Log out
        </button>
      </aside>
      <main className="flex-1 p-8 overflow-auto">
        {maintenance && (
          <div className="mb-5 rounded-xl bg-[#A53A2A] text-white px-4 py-2.5 flex items-center justify-between gap-3 shadow-sm"
            data-testid="admin-maintenance-banner">
            <div className="flex items-center gap-2 text-sm">
              <span className="inline-block h-2 w-2 rounded-full bg-white animate-pulse" />
              <b>Maintenance Mode is ON</b>
              <span className="opacity-80 hidden sm:inline">— public site is showing the maintenance page. Admin access is unaffected.</span>
            </div>
            <Link to={adminUrl("/settings")} className="text-xs underline hover:text-[#C89B3C] font-semibold whitespace-nowrap">
              Manage →
            </Link>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}

export function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats-v2").then((r) => setStats(r.data)); }, []);

  if (!stats) return <div>Loading…</div>;

  // Distinction (Feb 2026): Total Memberships = privilege CARDS issued
  // (1 per family); Total Members = sum of primary + sub-members (humans).
  // For Family plans 1 membership → 4 members, so the two numbers diverge.
  const cards = [
    ["Total Memberships", stats.total_memberships, "text-[#0F2540]"],
    ["Total Members", stats.total_members, "text-[#0F2540]"],
    ["Active Membership", stats.active_memberships ?? stats.active_members, "text-[#1F7A3D]"],
    ["Expired Membership", stats.expired_memberships ?? stats.expired_members, "text-[#E63946]"],
    ["Pending KYC", stats.kyc_pending, "text-[#C89B3C]"],
    ["KYC Verified", stats.kyc_verified, "text-[#1F7A3D]"],
    ["Today Enrolments", stats.today_enrolments, "text-[#1E4F87]"],
    ["Revenue (Lifetime)", inr(stats.revenue), "text-[#A53A2A]"],
    ["Revenue (This Month)", inr(stats.revenue_month ?? 0), "text-[#A53A2A]"],
    ["Pending Renewals (30d)", stats.pending_renewals, "text-[#C89B3C]"],
    ["Discount Code Sales", stats.referral_sales, "text-[#1E4F87]"],
    ["Corporate Leads", stats.corporate_leads, "text-[#0F2540]"],
  ];
  const PIE_COLORS = ["#1E4F87", "#A53A2A", "#C89B3C", "#1F7A3D", "#0F2540"];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-3xl font-bold text-[#0F2540]">Dashboard</h1>
      <p className="text-[#5C6B7A]">A snapshot of the business today.</p>
      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(([l, v, c]) => (
          <div key={l} className="rounded-2xl bg-white border border-[#E1E8F0] p-5" data-testid={`stat-${l.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="text-xs uppercase tracking-wider text-[#5C6B7A]">{l}</div>
            <div className={`mt-2 font-display text-2xl font-bold ${c}`}>{v}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-white border border-[#E1E8F0] p-6" data-testid="chart-revenue">
          <h3 className="font-display text-lg font-bold text-[#0F2540]">Revenue · monthly</h3>
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.monthly_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E1E8F0" />
                <XAxis dataKey="month" stroke="#5C6B7A" fontSize={12} />
                <YAxis stroke="#5C6B7A" fontSize={12} />
                <Tooltip formatter={(v) => inr(v)} />
                <Bar dataKey="amount" fill="#1E4F87" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl bg-white border border-[#E1E8F0] p-6" data-testid="chart-plans">
          <h3 className="font-display text-lg font-bold text-[#0F2540]">Plan distribution</h3>
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stats.plan_dist} dataKey="value" nameKey="name" outerRadius={90} label>
                  {stats.plan_dist.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <Leaderboards />
      <EngagementGate><EngagementAnalytics /></EngagementGate>
    </div>
  );
}

function EngagementGate({ children }) {
  // Hides children when the engagement module master switch is OFF.
  const [show, setShow] = React.useState(null); // null = loading
  React.useEffect(() => {
    let alive = true;
    api.get("/maintenance-status")
      .then((r) => { if (alive) setShow(!!r.data.engagement_module_enabled); })
      .catch(() => { if (alive) setShow(false); });
    return () => { alive = false; };
  }, []);
  if (!show) return null;
  return children;
}


function Leaderboards() {
  const [period, setPeriod] = useState("all_time");
  const [data, setData] = useState({ staff: [], members: [] });

  useEffect(() => {
    const p = period === "this_month" ? "?period=this_month" : "";
    api.get(`/admin/leaderboard${p}`).then((r) => setData(r.data));
  }, [period]);

  return (
    <div className="mt-8" data-testid="dashboard-leaderboards">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="font-display text-xl font-bold text-[#0F2540]">Top Discount Codes</h3>
        <div className="inline-flex rounded-xl border border-[#E1E8F0] bg-white p-1">
          {[
            ["all_time", "All time"],
            ["this_month", "This month"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setPeriod(k)}
              className={`text-xs px-3 py-1.5 rounded-lg font-semibold ${period === k ? "bg-[#1E4F87] text-white" : "text-[#5C6B7A] hover:text-[#0F2540]"}`}
              data-testid={`leaderboard-period-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <LeaderboardCard title="Hospital Staff" rows={data.staff} testid="leaderboard-staff" />
        <LeaderboardCard title="Members" rows={data.members} testid="leaderboard-members" />
      </div>
    </div>
  );
}

function LeaderboardCard({ title, rows, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E1E8F0] p-5" data-testid={testid}>
      <h4 className="text-sm font-bold text-[#0F2540] flex items-center justify-between">
        <span>{title}</span>
        <span className="text-xs text-[#5C6B7A] font-normal">{rows.length} referrer{rows.length !== 1 ? "s" : ""}</span>
      </h4>
      {rows.length === 0 ? (
        <div className="text-sm text-[#5C6B7A] mt-4 text-center py-6">No discount code usage yet.</div>
      ) : (
        <ol className="mt-3 space-y-1">
          {rows.map((row, i) => (
            <li key={row.referral_code} className="flex items-center justify-between rounded-xl px-3 py-2 hover:bg-[#F4F7FB]" data-testid={`${testid}-row-${i}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className={`h-6 w-6 rounded-full grid place-items-center text-xs font-bold ${i < 3 ? "bg-[#C89B3C] text-white" : "bg-[#EAF1FA] text-[#1E4F87]"}`}>
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#0F2540] truncate">{row.name}</div>
                  <div className="text-[10px] font-mono text-[#5C6B7A]">{row.referral_code}{row.department ? ` · ${row.department}` : ""}</div>
                </div>
              </div>
              <div className="text-right pl-3">
                <div className="font-display text-lg font-bold text-[#1E4F87]">{row.count}</div>
                <div className="text-[10px] text-[#5C6B7A]">{inr(row.revenue || 0)}</div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [filter, setFilter] = useState("");

  const load = () => {
    const params = { page, limit };
    if (filter) params.source = filter;
    return api.get("/leads", { params }).then((r) => {
      setLeads(r.data.items);
      setTotal(r.data.total);
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, page, limit]);
  useEffect(() => { setPage(1); }, [filter]);

  const updateStatus = async (id, status) => {
    await api.put(`/leads/${id}`, { status });
    toast.success("Updated");
    load();
  };
  const addNote = async (id) => {
    const note = prompt("Add a note");
    if (!note) return;
    await api.put(`/leads/${id}`, { notes: note });
    toast.success("Note added");
    load();
  };

  return (
    <div data-testid="admin-leads">
      <h1 className="font-display text-3xl font-bold text-[#0F2540]">Leads CRM</h1>
      <div className="mt-4 flex gap-2">
        {["", "Contact Form", "Membership Enquiry"].map((f) => (
          <button key={f || "all"} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-sm ${filter === f ? "bg-[#1E4F87] text-white" : "bg-white border border-[#E1E8F0]"}`}
            data-testid={`filter-${f || "all"}`}>
            {f || "All"}
          </button>
        ))}
      </div>
      <div className="mt-6 space-y-3">
        {leads.map((l) => (
          <div key={l.lead_id} className="rounded-2xl bg-white border border-[#E1E8F0] p-5" data-testid={`lead-${l.lead_id}`}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="font-semibold text-[#0F2540]">{l.full_name}</div>
                <div className="text-sm text-[#5C6B7A]">{l.email} · {l.mobile} {l.city ? `· ${l.city}` : ""}</div>
                <div className="text-xs text-[#5C6B7A] mt-1">Source: <span className="font-semibold">{l.source}</span>{l.interested_plan ? ` · Interested: ${l.interested_plan}` : ""}</div>
                {l.message && <p className="mt-2 text-sm text-[#0F2540]">{l.message}</p>}
                {l.notes?.length > 0 && (
                  <ul className="mt-2 text-xs text-[#5C6B7A] space-y-1">
                    {l.notes.map((n, i) => <li key={i}>• {n.note} <span className="opacity-60">· {n.by}</span></li>)}
                  </ul>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <select value={l.status} onChange={(e) => updateStatus(l.lead_id, e.target.value)}
                  className="px-3 py-1.5 rounded-full text-sm border border-[#E1E8F0] bg-white">
                  {["New", "Contacted", "Interested", "Follow Up", "Converted", "Closed"].map((s) => <option key={s}>{s}</option>)}
                </select>
                <button onClick={() => addNote(l.lead_id)} className="text-sm underline text-[#1E4F87]">Add note</button>
              </div>
            </div>
          </div>
        ))}
        {leads.length === 0 && <p className="text-[#5C6B7A]">No leads.</p>}
      </div>
      <Pagination page={page} limit={limit} total={total} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} testid="leads-pagination" />
    </div>
  );
}

export function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/plans?all=true").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);

  const save = async (p) => {
    const finalPrice = Number(p.final_price ?? p.price ?? 0);
    const gst = Number(p.gst_percent ?? 18);
    const payload = {
      name: p.name, tier: p.tier,
      final_price: finalPrice, gst_percent: gst,
      duration_months: Number(p.duration_months || 12),
      family_members: Number(p.family_members || 1),
      benefits: (p.benefits || "").toString().split(/\n/).map((x) => x.trim()).filter(Boolean),
      tagline: p.tagline, is_active: !!p.is_active, highlight: !!p.highlight,
      hsn_sac: p.hsn_sac || "",
    };
    if (p.plan_id) await api.put(`/plans/${p.plan_id}`, payload);
    else await api.post("/plans", payload);
    toast.success("Saved");
    setEditing(null); load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete plan?")) return;
    await api.delete(`/plans/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-plans">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold text-[#0F2540]">Plans</h1>
        <button className="btn-primary" onClick={() => setEditing({ tier: "individual", final_price: 2360, gst_percent: 18, duration_months: 12, family_members: 1, is_active: true })} data-testid="new-plan-btn">
          New plan
        </button>
      </div>

      {editing && (
        <div className="mt-6 rounded-2xl bg-white border border-[#E1E8F0] p-6 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Name" v={editing.name} s={(v) => setEditing({ ...editing, name: v })} />
            <F label="Tier (individual / husband_wife / family)" v={editing.tier} s={(v) => setEditing({ ...editing, tier: v })} />
            <F label="Final Price (incl. GST)" type="number" v={editing.final_price ?? editing.price} s={(v) => setEditing({ ...editing, final_price: v })} />
            <F label="GST %" type="number" v={editing.gst_percent ?? 18} s={(v) => setEditing({ ...editing, gst_percent: v })} />
            <div>
              <label className="text-sm font-medium text-[#5C6B7A]">Base Price (auto · excl. GST)</label>
              <input readOnly value={inr((Number(editing.final_price ?? editing.price ?? 0) / (1 + Number(editing.gst_percent ?? 18) / 100)))}
                className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-[#F8FAFC] px-4 py-2.5 font-semibold text-[#0F2540]" data-testid="plan-base-readonly" />
            </div>
            <F label="Duration (months)" type="number" v={editing.duration_months} s={(v) => setEditing({ ...editing, duration_months: v })} />
            <F label="Member Limit" type="number" v={editing.family_members} s={(v) => setEditing({ ...editing, family_members: v })} />
            <F label="HSN/SAC code" v={editing.hsn_sac || ""} s={(v) => setEditing({ ...editing, hsn_sac: v })} />
            <F label="Tagline" v={editing.tagline} s={(v) => setEditing({ ...editing, tagline: v })} />
          </div>
          <div>
            <label className="text-sm font-medium">Benefits (one per line)</label>
            <textarea rows={5} value={Array.isArray(editing.benefits) ? editing.benefits.join("\n") : (editing.benefits || "")}
              onChange={(e) => setEditing({ ...editing, benefits: e.target.value })}
              className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5" />
          </div>
          <div className="flex items-center gap-6 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.highlight} onChange={(e) => setEditing({ ...editing, highlight: e.target.checked })} /> Highlight</label>
          </div>
          <div className="flex gap-2">
            <button onClick={() => save(editing)} className="btn-primary">Save</button>
            <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {plans.map((p) => (
          <div key={p.plan_id} className="rounded-2xl bg-white border border-[#E1E8F0] p-5">
            <div className="flex justify-between">
              <div>
                <div className="font-display text-xl text-[#0F2540]">{p.name} <span className="text-xs uppercase opacity-60">({p.tier})</span></div>
                <div className="text-sm text-[#5C6B7A]">{inr(p.base_price ?? p.price)} + {p.gst_percent ?? 18}% GST = {inr(p.price)} · {p.duration_months}mo · {p.family_members} members</div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${p.is_active ? "bg-[#1F7A3D]/10 text-[#1F7A3D]" : "bg-[#E63946]/10 text-[#E63946]"}`}>
                {p.is_active ? "Active" : "Inactive"}
              </span>
            </div>
            <ul className="mt-2 text-xs text-[#5C6B7A] list-disc pl-5">
              {p.benefits.slice(0, 4).map((b, i) => <li key={i}>{b}</li>)}
            </ul>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setEditing(p)} className="text-sm underline text-[#1E4F87]" data-testid={`edit-plan-${p.plan_id}`}>Edit</button>
              <button onClick={() => del(p.plan_id)} className="text-sm underline text-[#E63946]">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminMembers() {
  const { user } = useAuth();
  const canRegister = hasCap("offlineRegister", user?.role);
  const canEdit = hasCap("editMember", user?.role);
  // Sensitive identity fields (name/email/dob/gender/aadhaar, member relation) —
  // only super_admin/admin may edit. Staff get read-only on these.
  const canEditFull = hasCap("editMemberFull", user?.role);
  // UHID — locked once entered; only super_admin/admin can ever modify it.
  const canEditUhid = hasCap("editUhid", user?.role);
  const canResend = hasCap("resendEmail", user?.role);
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  // Snapshot of `editing` at modal open — used to detect unsaved edits on close.
  const [editingSnapshot, setEditingSnapshot] = useState(null);
  // Original values captured at modal-open. Used so that lock-once-entered
  // decisions (UHID / Aadhaar) reference the persisted DB value, not the
  // live in-progress state — otherwise the field disables itself after the
  // first keystroke. Keyed by member_id; top-level mirrored from primary.
  const [editingOriginals, setEditingOriginals] = useState(null);
  const [offline, setOffline] = useState(null); // offline enrol form state
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [memberTab, setMemberTab] = useState("profile"); // profile | visits | benefits
  // Centralised close-with-unsaved-guard. Use this in place of `setEditing(null)`.
  const closeEditing = React.useCallback(() => {
    if (busy) return;
    if (editing && editingSnapshot && JSON.stringify(editing) !== editingSnapshot) {
      if (!window.confirm("You have unsaved changes. Discard them and close?")) return;
    }
    setEditing(null); setEditingSnapshot(null); setEditingOriginals(null);
    setMemberTab("profile");
  }, [busy, editing, editingSnapshot]);
  const [filter, setFilter] = useState({ period: "", from_date: "", to_date: "", q: "" });
  const load = () => api.get("/memberships", { params: { ...filter, page, limit } }).then((r) => {
    setMembers(r.data.items);
    setTotal(r.data.total);
  });
  const exportMembers = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });
      const query = params.toString();
      const filename = `NPC_Members_Export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      await downloadFile(`/memberships/export.xlsx${query ? `?${query}` : ""}`, filename);
      toast.success("Membership export downloaded");
    } catch (e) {
      let message = apiErr(e, "Could not export memberships");
      if (e.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await e.response.data.text());
          if (typeof body.detail === "string") message = body.detail;
        } catch (_parseError) {}
      }
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, page, limit]);
  useEffect(() => { setPage(1); }, [filter]);
  useEffect(() => { api.get("/plans?all=true").then((r) => setPlans(r.data.filter((p) => p.is_active))); }, []);

  // Extension modal state (#4 — collect reason + show history)
  const [extendModal, setExtendModal] = useState(null);     // {membership, days}
  const [extendHistory, setExtendHistory] = useState([]);
  const [extendReason, setExtendReason] = useState("");
  // Card modal — view/print/PDF/PNG of the membership card.
  const [cardFor, setCardFor] = useState(null);

  // Reset the inline "+ Add Member" draft whenever the edit modal is closed.
  useEffect(() => { if (!editing) setDraftMember(null); }, [editing]);

  const openExtend = async (m, days) => {
    setExtendModal({ membership: m, days });
    setExtendReason("");
    try {
      const r = await api.get(`/memberships/${m.membership_id}/extensions`);
      setExtendHistory(r.data || []);
    } catch (e) { console.error("extensions fetch failed", e); setExtendHistory([]); }
  };
  const confirmExtend = async () => {
    if (!extendReason.trim()) {
      toast.error("Please add a reason for this extension");
      return;
    }
    try {
      await api.put(`/memberships/${extendModal.membership.membership_id}`, {
        extend_days: extendModal.days,
        reason: extendReason.trim(),
      });
      toast.success(`Extended by ${extendModal.days} days`);
      setExtendModal(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const update = async (id, body) => {
    await api.put(`/memberships/${id}`, body); toast.success("Updated"); load();
  };

  const resendEmail = async (m) => {
    if (!window.confirm(`Resend membership email to ${m.email}?\n\n(This will re-deliver the digital card + invoice PDF.)`)) return;
    try {
      const r = await api.post("/admin/email/resend-membership", { membership_id: m.membership_id, force: true });
      if (r.data.skipped) toast.info("Already sent — used force=true");
      else toast.success(`Email queued · ${r.data.id?.slice(0, 8) || "ok"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const resetMemberPassword = async (m) => {
    const pwd = window.prompt(`Reset password for ${m.full_name} (${m.email}).\n\nEnter new password (min 8 chars):`);
    if (!pwd) return;
    if (pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      await api.post(`/admin/members/${m.member_id}/reset-password`, { new_password: pwd });
      toast.success(`Password reset for ${m.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  const saveProfile = async () => {
    if (!editing) return;
    // Mandatory + format gates — Gender, Aadhaar, Address, City, Pincode, State
    if (!editing.gender) return toast.error("Gender is required");
    if (editing.aadhaar && !/^\d{12}$/.test(editing.aadhaar)) {
      return toast.error("Aadhaar must be exactly 12 digits");
    }
    if (!(editing.address || "").trim()) return toast.error("Address is required");
    if (!(editing.city || "").trim()) return toast.error("City is required");
    if (!/^\d{6}$/.test(editing.pincode || "")) {
      return toast.error("Pincode must be exactly 6 digits");
    }
    if (!editing.state) return toast.error("State is required");
    // Pre-flight: re-check plan composition rules against the in-modal members[]
    const plan = plans.find((p) => p.plan_id === editing.plan_id);
    if (plan) {
      const allowed = Number(plan.family_members) || 1;
      const list = editing.members || [];
      if (list.length > allowed) {
        return toast.error(`This ${plan.name} plan allows up to ${allowed} member(s) — you have ${list.length}.`);
      }
      const subs = list.filter((x) => !x.is_primary).map((x) => ({
        relation: x.relation, dob: x.dob, full_name: x.full_name,
      }));
      const err = validateFamilyComposition(plan.tier, subs);
      if (err) return toast.error(err);
    }
    setBusy(true);
    try {
      const body = {
        full_name: editing.full_name, email: editing.email, mobile: editing.mobile,
        address: editing.address, dob: editing.dob, aadhaar: editing.aadhaar, gender: editing.gender,
        state: editing.state || null,
        city: editing.city || null,
        pincode: editing.pincode || null,
        // `family_members_count` is now derived from members[] on the backend —
        // we no longer send the manual override.
        family_member_names: editing.family_member_names || [],
        aadhaar_doc_id: editing.aadhaar_doc_id || null,
        photo_doc_id: editing.photo_doc_id || null,
        uhid: editing.uhid || null,
      };
      await api.put(`/memberships/${editing.membership_id}/profile`, body);
      // Persist any edits on each *sub-member* row.
      // CRITICAL: skip the primary here. The /profile PUT above already handles
      // the primary's fields, and `_patch_sub_member` for the primary writes
      // back to the top-level fields — which would overwrite the just-saved
      // values with whatever stale data sits in `editing.members[0]`.
      for (const sm of (editing.members || [])) {
        if (sm.is_primary || sm.member_id === editing.member_id) continue;
        await api.put(`/memberships/${editing.membership_id}/members/${sm.member_id}`, {
          full_name: sm.full_name, mobile: sm.mobile, dob: sm.dob, relation: sm.relation,
          aadhaar: sm.aadhaar || null,
          aadhaar_doc_id: sm.aadhaar_doc_id || null,
          photo_doc_id: sm.photo_doc_id || null,
          uhid: sm.uhid || null,
        });
      }
      toast.success("Profile updated");
      setEditing(null); setEditingSnapshot(null); setEditingOriginals(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  // Inline "+ Add Member" — manages a draft row directly in the modal (no prompts).
  const [draftMember, setDraftMember] = useState(null);
  const beginAddMember = () => {
    if (!editing) return;
    const plan = plans.find((p) => p.plan_id === editing.plan_id);
    const allowed = Number(plan?.family_members) || 1;
    const currentTotal = (editing.members || []).length || Number(editing.total_members) || 1;
    if (currentTotal >= allowed) {
      return toast.error(`Member limit reached — this plan allows ${allowed} member(s).`);
    }
    setDraftMember({ full_name: "", relation: "", mobile: "", dob: "", aadhaar: "", uhid: "" });
  };
  const cancelAddMember = () => setDraftMember(null);
  const saveDraftMember = async () => {
    if (!editing || !draftMember) return;
    const d = draftMember;
    if (!(d.full_name || "").trim()) return toast.error("Full name is required");
    if (!d.relation) return toast.error("Relation is required");
    // Mobile is OPTIONAL for non-primary members — validate format only when supplied.
    if (d.mobile && !/^\d{10}$/.test(d.mobile)) return toast.error("Mobile must be 10 digits");
    if (!d.dob) return toast.error("Date of birth is required");
    if (!/^\d{12}$/.test(d.aadhaar || "")) return toast.error("Aadhaar must be exactly 12 digits");
    // Duplicate Aadhaar check (across all memberships + sub-members)
    try {
      const dr = await api.post("/check-duplicate", { aadhaar: d.aadhaar });
      if (dr.data?.duplicate) return toast.error(dr.data.message || "Aadhaar already registered");
    } catch (e) { console.error("dup-check (member edit) failed", e); /* network blip — backend will still guard */ }
    // Pre-flight composition rules — reuse the existing helper.
    const plan = plans.find((p) => p.plan_id === editing.plan_id);
    if (plan) {
      const existingSubs = (editing.members || [])
        .filter((x) => !x.is_primary)
        .map((x) => ({ relation: x.relation, dob: x.dob, full_name: x.full_name }));
      const proposed = [...existingSubs, {
        relation: d.relation, dob: d.dob, full_name: d.full_name,
      }];
      const err = validateFamilyComposition(plan.tier, proposed);
      if (err) return toast.error(err);
    }
    try {
      const r = await api.post(`/memberships/${editing.membership_id}/members`, d);
      // Backend returns { ok, member, membership } — pull the refreshed membership
      // so the modal members[] reflects the new row immediately.
      setEditing(r.data.membership || r.data);
      setDraftMember(null);
      toast.success("Member added");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add member"); }
  };

  const removeEditMember = async (sm) => {
    if (!editing || !sm) return;
    if (sm.is_primary || sm.member_id === editing.member_id) {
      return toast.error("Primary member cannot be removed.");
    }
    if (!window.confirm(`Remove ${sm.full_name || sm.relation || "this member"} from the membership?`)) return;
    try {
      const r = await api.delete(`/memberships/${editing.membership_id}/members/${sm.member_id}`);
      setEditing(r.data);
      toast.success("Member removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to remove member"); }
  };

  const uploadDocForSub = async (idx, key, file) => {
    if (!file || !editing) return;
    try {
      const data_base64 = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
      });
      const folder = key === "photo_doc_id" ? "members" : "aadhaar";
      const r = await api.post("/uploads/document", { filename: file.name, content_type: file.type, data_base64, folder });
      const newDocId = r.data.doc_id;

      // Identify whether this row is the primary — primary's docs live at the
      // top level of the membership; sub-members keep them on their own row.
      const target = (editing.members || [])[idx] || {};
      const isPrimary = target.is_primary || target.member_id === editing.member_id;

      // Persist immediately so the Verify gate sees the new doc without a
      // separate Save → Reopen round-trip.
      let updated;
      if (isPrimary) {
        // Use the same /profile body shape as saveEdit to avoid wiping fields:
        // only the doc field is changing, everything else is echoed back.
        const body = {
          full_name: editing.full_name, email: editing.email, mobile: editing.mobile,
          address: editing.address, dob: editing.dob, aadhaar: editing.aadhaar,
          gender: editing.gender,
          state: editing.state || null, city: editing.city || null, pincode: editing.pincode || null,
          family_member_names: editing.family_member_names || [],
          aadhaar_doc_id: key === "aadhaar_doc_id" ? newDocId : (editing.aadhaar_doc_id || null),
          photo_doc_id:   key === "photo_doc_id"   ? newDocId : (editing.photo_doc_id || null),
          uhid: editing.uhid || null,
        };
        const resp = await api.put(`/memberships/${editing.membership_id}/profile`, body);
        updated = resp.data;
      } else {
        const resp = await api.put(`/memberships/${editing.membership_id}/members/${target.member_id}`, {
          full_name: target.full_name, mobile: target.mobile, dob: target.dob, relation: target.relation,
          aadhaar: target.aadhaar || null,
          aadhaar_doc_id: key === "aadhaar_doc_id" ? newDocId : (target.aadhaar_doc_id || null),
          photo_doc_id:   key === "photo_doc_id"   ? newDocId : (target.photo_doc_id || null),
          uhid: target.uhid || null,
        });
        updated = resp.data;
      }
      // Refresh the modal with the canonical server state so Verify lights up
      // immediately and the UI never drifts from the DB.
      setEditing(updated);
      toast.success("Uploaded & saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  // Admin KYC verify/reject — calls dedicated endpoint and updates editing state.
  // For "verified": auto-persist unsaved UHID/Aadhaar changes from the local
  // editing state FIRST so the user doesn't get a confusing 400 "UHID not
  // entered" right after typing it. (Backend reads from DB, frontend only
  // had the value in memory — was the user-reported confusion.)
  const verifyKyc = async (subMemberId, status, remarks) => {
    if (!editing) return;
    try {
      if (status === "verified") {
        // Find the sub-member in the local editing state
        const subInState = (editing.members || []).find((x) => x.member_id === subMemberId);
        const isPrimary = subMemberId === editing.member_id || subInState?.is_primary;
        const subUhid = (subInState?.uhid || "").trim();
        const topUhid = (editing.uhid || "").trim();
        const subAadhaar = (subInState?.aadhaar || "").trim();
        const topAadhaar = (editing.aadhaar || "").trim();
        // Persist UHID if it's set locally — covers both the primary (top-level
        // field, edited from the Hospital section) and any sub-member (edited
        // inline in the row). Then for primaries we also push aadhaar via the
        // same profile endpoint. For sub-members, fields land on the per-sub
        // PUT below.
        if (isPrimary && (topUhid || subUhid || topAadhaar || subAadhaar)) {
          const profilePatch = {};
          if (topUhid) profilePatch.uhid = topUhid;
          else if (subUhid) profilePatch.uhid = subUhid;
          if (topAadhaar) profilePatch.aadhaar = topAadhaar;
          else if (subAadhaar) profilePatch.aadhaar = subAadhaar;
          if (Object.keys(profilePatch).length) {
            try { await api.put(`/memberships/${editing.membership_id}/profile`, profilePatch); }
            catch (_) { /* surfaced by the verify call below */ }
          }
        } else if (!isPrimary && (subUhid || subAadhaar)) {
          const subPatch = {};
          if (subUhid) subPatch.uhid = subUhid;
          if (subAadhaar) subPatch.aadhaar = subAadhaar;
          try {
            await api.put(
              `/memberships/${editing.membership_id}/members/${subMemberId}`,
              subPatch,
            );
          } catch (_) { /* fall through, verify will surface */ }
        }
      }
      const r = await api.put(
        `/memberships/${editing.membership_id}/members/${subMemberId}/kyc`,
        { status, remarks: remarks || "" },
      );
      setEditing(r.data);
      toast.success(`KYC ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update KYC");
    }
  };

  const viewDoc = async (docId) => {
    if (!docId) return;
    try {
      const r = await api.get(`/uploads/document/${docId}`);
      const src = r.data.url
        ? r.data.url
        : (r.data.data_base64 ? `data:${r.data.content_type};base64,${r.data.data_base64}` : null);
      if (!src) { toast.error("Document unavailable"); return; }
      const isPdf = r.data.content_type === "application/pdf";
      const w = window.open("", "_blank", "width=900,height=900");
      if (!w) { toast.error("Pop-up blocked"); return; }
      // Safe DOM rendering — avoids document.write() XSS via filename
      try {
        w.document.title = String(r.data.filename || "Document");
        const body = w.document.body;
        if (isPdf) {
          const iframe = w.document.createElement("iframe");
          iframe.src = src;
          iframe.style.cssText = "border:0;width:100%;height:100vh";
          body.appendChild(iframe);
        } else {
          body.style.cssText = "margin:0;background:#0F2540;display:grid;place-items:center";
          const img = w.document.createElement("img");
          img.src = src;
          img.style.cssText = "max-width:96vw;max-height:96vh";
          body.appendChild(img);
        }
      } catch (renderErr) {
        console.error("Document viewer render failed", renderErr);
        try { w.close(); } catch (e) { /* ignore */ }
        toast.error("Could not open document");
      }
    } catch (e) { console.error("viewDoc failed", e); toast.error("Could not open document"); }
  };

  // --- Offline Enrolment ---
  const [offDupErrors, setOffDupErrors] = useState({});
  const [offFieldErrors, setOffFieldErrors] = useState({});
  const [offPricing, setOffPricing] = useState(null);
  const [offRefStatus, setOffRefStatus] = useState(null); // {valid, member_name, discount_percent}
  const [offRefBusy, setOffRefBusy] = useState(false);
  const openOffline = () => {
    setOffDupErrors({}); setOffFieldErrors({});
    setOffPricing(null); setOffRefStatus(null);
    setOffline({
      step: 1,
      plan_id: plans[0]?.plan_id || "", full_name: "", email: "", mobile: "", address: "", dob: "",
      gender: "", aadhaar: "", gst_number: "", referral_code: "", method: "cash", amount: "",
      state: "", city: "", pincode: "",
      family_members: [],
    });
  };

  // Reset discount status whenever the code changes (forces re-validate)
  useEffect(() => {
    setOffRefStatus(null);
  }, [offline?.referral_code]);

  // Live pricing preview whenever we land on Step 4 or the discount status changes
  useEffect(() => {
    if (!offline || offline.step !== 4 || !offline.plan_id) { setOffPricing(null); return; }
    const code = offRefStatus?.valid ? offline.referral_code : "";
    api.get(`/pricing/preview?plan_id=${offline.plan_id}${code ? `&referral_code=${encodeURIComponent(code)}` : ""}`)
      .then((r) => setOffPricing(r.data))
      .catch(() => setOffPricing(null));
  }, [offline?.step, offline?.plan_id, offRefStatus, offline?.referral_code]);

  const validateOffDiscount = async () => {
    const code = (offline?.referral_code || "").trim();
    if (!code) { setOffRefStatus({ valid: false, message: "Enter a discount code first." }); return; }
    setOffRefBusy(true);
    try {
      const r = await api.get(`/referrals/verify/${encodeURIComponent(code)}`);
      // Backend returns 200 with {valid: false} when the code doesn't exist —
      // we must NOT treat that as success.
      if (r.data?.valid) {
        setOffRefStatus({ valid: true, ...r.data });
        const pct = Number(r.data.discount_percent);
        toast.success(`Discount applied${Number.isFinite(pct) ? ` · ${pct}% off` : ""}`);
      } else {
        setOffRefStatus({ valid: false, message: r.data?.message || "Invalid discount code" });
        toast.error(r.data?.message || "Invalid discount code");
      }
    } catch (e) {
      setOffRefStatus({ valid: false, message: e.response?.data?.detail || "Invalid code" });
      toast.error(e.response?.data?.detail || "Invalid discount code");
    } finally { setOffRefBusy(false); }
  };
  const offPlan = useMemo(() => plans.find((p) => p.plan_id === offline?.plan_id), [plans, offline?.plan_id]);
  const offMaxFamily = Math.max(0, (Number(offPlan?.family_members) || 1) - 1);
  const offUsedSlots = 1 + (offline?.family_members?.length || 0);
  const offCanAdd = (offline?.family_members?.length || 0) < offMaxFamily;

  // Realtime duplicate check on blur (mirrors JoinNPC `runDupCheck`).
  const runOffDupCheck = async (field) => {
    if (!offline) return;
    const value = offline[field] || "";
    if (field === "mobile" && !/^\d{10}$/.test(value)) return;
    if (field === "aadhaar" && !/^\d{12}$/.test(value)) return;
    if (field === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) return;
    if (field === "dob" && !value) return;
    try {
      const r = await api.post("/check-duplicate", {
        email:   field === "email"   ? value : "",
        mobile:  field === "mobile"  ? value : "",
        aadhaar: field === "aadhaar" ? value : "",
        dob:     field === "dob"     ? value : "",
      });
      setOffDupErrors((prev) => ({ ...prev, [field]: r.data }));
      if (r.data.duplicate) toast.error(r.data.message);
    } catch (e) { console.error("offline dup-check failed", e); /* network blip — final submit will surface */ }
  };

  // Per-field synchronous validation (typing + blur)
  const validateOffField = (field, value) => {
    if (field === "email"   && value && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) return "Enter a valid email address.";
    if (field === "mobile"  && value && !/^\d{10}$/.test(value)) return "Mobile must be 10 digits.";
    if (field === "aadhaar" && value && !/^\d{12}$/.test(value)) return "Aadhaar must be exactly 12 digits.";
    if (field === "pincode" && value && !/^\d{6}$/.test(value))  return "Pincode must be exactly 6 digits.";
    return "";
  };
  const updateOff = (field, value) => {
    setOffline((prev) => ({ ...prev, [field]: value }));
    setOffFieldErrors((prev) => ({ ...prev, [field]: validateOffField(field, value) }));
    // Clear dup error when the user edits the value (forces re-check on blur)
    setOffDupErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const addOffFam = () => {
    if (!offCanAdd) {
      return toast.error(`This plan allows up to ${offMaxFamily + 1} member${offMaxFamily ? "s" : ""}.`);
    }
    setOffline({ ...offline, family_members: [...offline.family_members, { full_name: "", mobile: "", dob: "", relation: "", aadhaar: "", uhid: "" }] });
  };
  const setOffFam = (i, patch) => {
    const arr = [...offline.family_members]; arr[i] = { ...arr[i], ...patch };
    setOffline({ ...offline, family_members: arr });
  };
  const rmOffFam = (i) => setOffline({ ...offline, family_members: offline.family_members.filter((_, j) => j !== i) });

  // When the plan changes, trim family rows down to the new plan's allowance.
  const setOffPlan = (plan_id) => {
    const p = plans.find((x) => x.plan_id === plan_id);
    const max = Math.max(0, (Number(p?.family_members) || 1) - 1);
    setOffline({ ...offline, plan_id, family_members: (offline.family_members || []).slice(0, max) });
  };

  const offStepValid = () => {
    if (!offline) return false;
    if (offline.step === 1) return !!offline.plan_id && !!offline.method;
    if (offline.step === 2) {
      const required = ["full_name", "email", "mobile", "dob", "gender", "aadhaar",
                        "address", "city", "pincode", "state"];
      if (required.some((k) => !(offline[k] || "").toString().trim())) return false;
      if (offline.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(offline.email)) return false;
      if (!/^\d{10}$/.test(offline.mobile)) return false;
      if (!/^\d{12}$/.test(offline.aadhaar)) return false;
      if (!/^\d{6}$/.test(offline.pincode)) return false;
      // Block on any unresolved duplicate detected by realtime check.
      if (["email", "mobile", "aadhaar"].some((k) => offDupErrors[k]?.duplicate)) return false;
      return true;
    }
    if (offline.step === 3) {
      // Each present row must have name + dob + 12-digit aadhaar (mobile optional)
      const all = (offline.family_members || []).every((f) =>
        f.full_name && f.dob && /^\d{12}$/.test(f.aadhaar || ""));
      return all && offUsedSlots <= (Number(offPlan?.family_members) || 1);
    }
    return true;
  };
  const offNext = () => {
    if (offline?.step === 2) {
      // Helpful field-specific messages (instead of the generic "fill highlighted").
      const issues = [];
      const need = { full_name: "Full name", email: "Email", mobile: "Mobile", dob: "Date of Birth",
                     gender: "Gender", aadhaar: "Aadhaar", address: "Address",
                     city: "City", pincode: "Pincode", state: "State" };
      Object.entries(need).forEach(([k, label]) => {
        if (!(offline[k] || "").toString().trim()) issues.push(`${label} is required.`);
      });
      ["email", "mobile", "aadhaar", "pincode"].forEach((k) => {
        const fe = validateOffField(k, offline[k]);
        if (fe) issues.push(fe);
      });
      ["email", "mobile", "aadhaar"].forEach((k) => {
        if (offDupErrors[k]?.duplicate) issues.push(offDupErrors[k].message);
      });
      if (issues.length) {
        toast.error(issues[0]);
        return;
      }
    } else if (!offStepValid()) {
      return toast.error("Please fill the highlighted fields");
    }
    setOffline({ ...offline, step: Math.min(4, offline.step + 1) });
  };
  const offBack = () => setOffline({ ...offline, step: Math.max(1, offline.step - 1) });

  const submitOffline = async () => {
    // Final gate — same rules as Step 2 next() so a direct submit can't bypass.
    const missing = [];
    const need = { full_name: "Full name", email: "Email", mobile: "Mobile", dob: "Date of Birth",
                   gender: "Gender", aadhaar: "Aadhaar", address: "Address",
                   city: "City", pincode: "Pincode", state: "State" };
    Object.entries(need).forEach(([k, label]) => {
      if (!(offline[k] || "").toString().trim()) missing.push(label);
    });
    if (missing.length) return toast.error(`Missing: ${missing.join(", ")}`);
    // Email is OPTIONAL for offline-enrol — admins routinely register members who only share a mobile.
    if (offline.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(offline.email)) return toast.error("Enter a valid email address (or leave blank)");
    if (!/^\d{10}$/.test(offline.mobile)) return toast.error("Mobile must be 10 digits");
    if (!/^\d{12}$/.test(offline.aadhaar)) return toast.error("Aadhaar must be exactly 12 digits");
    if (!/^\d{6}$/.test(offline.pincode)) return toast.error("Pincode must be exactly 6 digits");
    if (offUsedSlots > (Number(offPlan?.family_members) || 1)) {
      return toast.error(`Plan allows up to ${offPlan.family_members} member(s) — please remove extra rows`);
    }
    for (let i = 0; i < (offline.family_members || []).length; i++) {
      const fm = offline.family_members[i];
      if (!fm.full_name?.trim()) return toast.error(`Member ${i + 2}: full name is required`);
      if (!fm.relation) return toast.error(`Member ${i + 2}: relation is required`);
      if (!fm.dob) return toast.error(`Member ${i + 2}: date of birth is required`);
      if (fm.mobile && !/^\d{10}$/.test(fm.mobile)) return toast.error(`Member ${i + 2}: mobile must be 10 digits`);
      if (!/^\d{12}$/.test(fm.aadhaar || "")) return toast.error(`Member ${i + 2}: Aadhaar must be exactly 12 digits`);
    }
    // Duplicate-Aadhaar check across all rows + system-wide
    const seenAadhaar = new Set();
    if (offline.aadhaar) seenAadhaar.add(offline.aadhaar);
    for (let i = 0; i < (offline.family_members || []).length; i++) {
      const a = offline.family_members[i].aadhaar;
      if (seenAadhaar.has(a)) return toast.error(`Member ${i + 2}: same Aadhaar repeated in this form`);
      seenAadhaar.add(a);
      try {
        const dr = await api.post("/check-duplicate", { aadhaar: a });
        if (dr.data?.duplicate) return toast.error(`Member ${i + 2}: ${dr.data.message || "Aadhaar already registered"}`);
      } catch (e) { console.error("offline family dup-check failed", e); /* network blip — backend will guard */ }
    }
    for (const k of ["email", "mobile", "aadhaar"]) {
      if (offDupErrors[k]?.duplicate) return toast.error(offDupErrors[k].message);
    }
    setBusy(true);
    try {
      const body = {
        plan_id: offline.plan_id,
        full_name: offline.full_name, email: offline.email, mobile: offline.mobile,
        address: offline.address, dob: offline.dob, gender: offline.gender,
        aadhaar: offline.aadhaar, gst_number: offline.gst_number || null,
        state: offline.state || "",
        city: offline.city || "",
        pincode: offline.pincode || "",
        // Always forward the typed referral_code — extension is granted whenever
        // the code resolves to a valid sharer at the backend, regardless of
        // whether the admin clicked the manual "Validate" button beforehand.
        // (Validation gate only controls the discount % preview, not extension.)
        referral_code: (offline.referral_code || "").trim() || null,
        method: offline.method,
        amount: offline.amount ? Number(offline.amount) : null,
        family_members: offline.family_members.filter((f) => f.full_name && f.dob),
        family_members_count: offline.family_members.length,
      };
      const r = await api.post("/memberships/offline-enrol", body);
      toast.success(`Registered · ${r.data.membership.member_id}`);
      setOffline(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-members">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold text-[#0F2540]">Members</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            value={filter.q || ""}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            placeholder="Search · name · email · mobile · UHID · NPC ID"
            className="rounded-xl border border-[#E1E8F0] px-3 py-2 text-sm min-w-[260px]"
            data-testid="members-search"
          />
          <DateFilter value={filter} onChange={setFilter} testid="members-filter" />
          <button
            type="button"
            onClick={exportMembers}
            disabled={exporting}
            className="inline-flex items-center gap-2 rounded-xl border border-[#1E4F87] px-3 py-2 text-sm font-semibold text-[#1E4F87] hover:bg-[#EAF1FA] disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="members-export-xlsx"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exporting ? "Exporting..." : "Export to Excel"}
          </button>
          {/* <select
            value={filter.engagement || ""}
            onChange={(e) => setFilter({ ...filter, engagement: e.target.value })}
            data-testid="members-engagement-filter"
            className="rounded-xl border border-[#E1E8F0] px-3 py-2 text-sm bg-white"
          >
            <option value="">All engagement</option>
            <option value="highly_active">Highly Active</option>
            <option value="active">Active</option>
            <option value="moderate">Moderate</option>
            <option value="at_risk">At Risk</option>
            <option value="inactive">Inactive</option>
            <option value="at_risk,inactive">At Risk + Inactive</option>
          </select> */}
          {canRegister && (
            <button className="btn-primary !px-4 !py-2" onClick={openOffline} data-testid="offline-enrol-btn">+ Offline Registration</button>
          )}
        </div>
      </div>
      <div className="mt-6 rounded-2xl bg-white border border-[#E1E8F0] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EAF1FA] text-[#5C6B7A]">
            <tr>
              <th className="text-left px-4 py-3">Membership ID</th>
              <th className="text-left px-4 py-3">Member ID</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Plan</th>
              <th className="text-left px-4 py-3">Valid till</th>
              <th className="text-left px-4 py-3">KYC</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.membership_id} className="border-t border-[#E1E8F0]" data-testid={`member-row-${m.member_id}`}>
                <td className="px-4 py-3 font-mono text-xs text-[#5C6B7A]">{m.membership_number || "—"}</td>
                <td className="px-4 py-3 font-mono">{m.member_id}</td>
                <td className="px-4 py-3">{m.full_name}<div className="text-xs text-[#5C6B7A]">{m.email} · {m.mobile}</div>{m.discount_code && <div className="text-[10px] text-[#A0741F] font-mono mt-1" data-testid={`discount-code-${m.member_id}`}>Code · {m.discount_code}</div>}</td>
                <td className="px-4 py-3">{m.plan_name}</td>
                <td className="px-4 py-3">{new Date(m.valid_till).toLocaleDateString("en-IN")}</td>
                <td className="px-4 py-3 whitespace-nowrap" data-testid={`kyc-cell-${m.member_id}`}>
                  {(() => {
                    const list = (m.members && m.members.length) ? m.members : [{ kyc_status: m.kyc_status }];
                    const verified = list.filter((x) => (x.kyc_status || "pending") === "verified").length;
                    const rejected = list.filter((x) => (x.kyc_status || "pending") === "rejected").length;
                    const tone = verified === list.length ? "bg-[#1F7A3D]/10 text-[#1F7A3D]"
                      : rejected > 0 ? "bg-[#A53A2A]/10 text-[#A53A2A]"
                      : "bg-[#C89B3C]/15 text-[#7A5A1F]";
                    return <span className={`text-xs px-2 py-1 rounded-full font-semibold ${tone}`}>{verified}/{list.length}</span>;
                  })()}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${m.status === "active" ? "bg-[#1F7A3D]/10 text-[#1F7A3D]" : "bg-[#E63946]/10 text-[#E63946]"}`}>{m.status}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {canEdit && <button onClick={() => {
                    const snap = { ...m };
                    setEditing(snap);
                    setEditingSnapshot(JSON.stringify(snap));
                    // Capture original UHID/Aadhaar values per member — frozen for
                    // the duration of the edit session. Lock-once decisions reference
                    // these (not live state — which would lock after first keystroke).
                    // Primary's canonical values live on the membership doc top-level,
                    // so fall back to those when members[primary].* is empty.
                    const subOrig = {};
                    for (const x of (m.members || [])) {
                      const isPrim = x.is_primary || x.member_id === m.member_id;
                      subOrig[x.member_id] = {
                        aadhaar: (x.aadhaar || (isPrim ? m.aadhaar : "") || ""),
                        uhid:    (x.uhid    || (isPrim ? m.uhid    : "") || ""),
                      };
                    }
                    setEditingOriginals({
                      aadhaar: m.aadhaar || "",
                      uhid: m.uhid || "",
                      members: subOrig,
                    });
                  }} className="text-xs underline text-[#1E4F87] mr-3" data-testid={`edit-member-${m.member_id}`}>Edit</button>}
                  <button onClick={() => setCardFor(m)} className="text-xs underline text-[#0F2540] mr-3" data-testid={`card-member-${m.member_id}`}>Card</button>
                  {/* +30d Extension — admin / super_admin only. */}
                  {canEditFull && <button onClick={() => openExtend(m, 30)} className="text-xs underline text-[#1E4F87] mr-3" data-testid={`extend-30d-${m.member_id}`}>+30d</button>}
                  {canResend && <button onClick={() => resendEmail(m)} className="text-xs underline text-[#A53A2A] mr-3" data-testid={`resend-email-${m.member_id}`}>Resend Email</button>}
                  {user?.role === "super_admin" && (
                    <button onClick={() => resetMemberPassword(m)} className="text-xs underline text-[#0F2540] mr-3" data-testid={`reset-pwd-${m.member_id}`}>Reset Pwd</button>
                  )}
                  {canEdit && <button onClick={() => update(m.membership_id, { status: m.status === "active" ? "suspended" : "active" })} className="text-xs underline text-[#E63946]" data-testid={`toggle-status-${m.member_id}`}>
                    {m.status === "active" ? "Suspend" : "Reactivate"}
                  </button>}
                </td>
              </tr>
            ))}
            {members.length === 0 && <tr><td colSpan="8" className="px-4 py-6 text-center text-[#5C6B7A]">No members yet.</td></tr>}
          </tbody>
        </table>
        <Pagination page={page} limit={limit} total={total} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} testid="members-pagination" />
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/55 grid place-items-center p-3 sm:p-6" onClick={(e) => { if (e.target === e.currentTarget) toast("Click the close × to exit — your edits are preserved."); }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-[1180px] max-h-[94vh] flex flex-col shadow-2xl" data-testid="member-edit-modal">
            {/* Sticky header */}
            <div className="px-7 py-4 border-b border-[#E1E8F0] flex items-center justify-between bg-white rounded-t-2xl">
              <div>
                <h3 className="font-display text-2xl font-bold text-[#0F2540]">Edit member</h3>
                <p className="text-xs text-[#5C6B7A] mt-0.5">{editing.full_name} · <span className="font-mono">{editing.member_id}</span></p>
              </div>
              <div className="flex items-center gap-3">
                {!canEditFull && (
                  <span className="hidden md:inline text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#C89B3C]/15 text-[#7A5A1F] font-bold" data-testid="role-readonly-pill">
                    Limited edit · {user?.role}
                  </span>
                )}
                <span className={`text-xs px-2 py-1 rounded-full ${editing.status === "active" ? "bg-[#1F7A3D]/10 text-[#1F7A3D]" : "bg-[#E63946]/10 text-[#E63946]"}`}>{editing.status}</span>
                <EngagementGate><EngagementBadge memberId={editing.member_id} /></EngagementGate>
                <button onClick={() => setCardFor(editing)} className="text-xs px-2.5 py-1 rounded-full bg-[#0F2540] text-white hover:bg-[#1E4F87]" data-testid="edit-open-card-btn">View card</button>
                <button onClick={closeEditing} className="text-[#5C6B7A] hover:text-[#E63946] text-2xl leading-none px-1" aria-label="Close" data-testid="edit-close">×</button>
              </div>
            </div>

            {/* Tabs · Profile · Visits · Benefits */}
            {/* <div className="px-7 border-b border-[#E1E8F0] bg-white flex gap-1">
              {[
                { id: "profile", label: "Profile" },
                { id: "visits", label: "Visits" },
                { id: "benefits", label: "Benefit Wallet" },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setMemberTab(t.id)}
                  data-testid={`member-tab-${t.id}`}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${memberTab === t.id ? "border-[#1E4F87] text-[#1E4F87]" : "border-transparent text-[#5C6B7A] hover:text-[#0F2540]"}`}
                >
                  {t.label}
                </button>
              ))}
            </div> */}

            {/* Visits panel */}
            {memberTab === "visits" && (
              <div className="px-7 py-6 overflow-y-auto flex-1" data-testid="member-visits-panel">
                <MemberVisitsPanel memberId={editing.member_id} membership={editing} />
              </div>
            )}

            {/* Benefits panel */}
            {memberTab === "benefits" && (
              <div className="px-7 py-6 overflow-y-auto flex-1" data-testid="member-benefits-panel">
                <MemberBenefitsPanel memberId={editing.member_id} />
              </div>
            )}

            {/* Profile body (default) */}
            <div className={memberTab === "profile" ? "contents" : "hidden"}>

            {/* Scrollable body */}
            <div className="px-7 py-6 overflow-y-auto flex-1">

              {/* Section: Primary member identity */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-[#1E4F87]">Primary member</h4>
                  <span className="flex-1 h-px bg-[#E1E8F0]" />
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <F label="Full name" disabled={!canEditFull} v={editing.full_name} s={(v) => setEditing({ ...editing, full_name: v })} />
                  <F label="Email" disabled={!canEditFull} v={editing.email} s={(v) => setEditing({ ...editing, email: v })} />
                  <F label="Mobile" v={editing.mobile} s={(v) => setEditing({ ...editing, mobile: v })} />
                  <F label="DOB" type="date" disabled={!canEditFull} v={editing.dob} s={(v) => setEditing({ ...editing, dob: v })} />
                  <div>
                    <label className="text-sm font-medium text-[#0F2540]">Gender<span className="text-[#E63946]"> *</span></label>
                    <select value={editing.gender || ""} disabled={!canEditFull} onChange={(e) => setEditing({ ...editing, gender: e.target.value })}
                      className={`mt-1 w-full rounded-xl border border-[#E1E8F0] px-3 py-2.5 ${canEditFull ? "bg-white" : "bg-[#F4F7FB] text-[#5C6B7A] cursor-not-allowed"}`} data-testid="edit-gender">
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  {/* Aadhaar + UHID for the primary live inside the
                      "Members & KYC tracking" section below — single source of truth. */}
                </div>
              </div>

              {/* Section: Address */}
              <div className="mt-7">
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-[#1E4F87]">Address</h4>
                  <span className="flex-1 h-px bg-[#E1E8F0]" />
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-3"><F label="Address" required v={editing.address} s={(v) => setEditing({ ...editing, address: v })} testid="edit-address" /></div>
                  <F label="City" required v={editing.city} s={(v) => setEditing({ ...editing, city: v })} testid="edit-city" />
                  <F label="Pincode (6-digit)" required v={editing.pincode} s={(v) => setEditing({ ...editing, pincode: (v || "").replace(/\D/g, "").slice(0, 6) })} testid="edit-pincode" />
                  <StateSelect value={editing.state} onChange={(v) => setEditing({ ...editing, state: v })} label="State *" testid="edit-state" />
                </div>
              </div>

              {/* Section: Members & KYC tracking */}
              <div className="mt-7">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                  <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                    <h4 className="text-sm font-bold uppercase tracking-wider text-[#1E4F87]">Members &amp; KYC tracking</h4>
                    <span className="flex-1 h-px bg-[#E1E8F0]" />
                  </div>
                  {(() => {
                    const list = (editing.members && editing.members.length)
                      ? editing.members
                      : [{ member_id: editing.member_id, is_primary: true,
                           full_name: editing.full_name, mobile: editing.mobile, dob: editing.dob,
                           aadhaar: editing.aadhaar, relation: "self",
                           aadhaar_doc_id: editing.aadhaar_doc_id, photo_doc_id: editing.photo_doc_id,
                           kyc_status: editing.kyc_status }];
                    const verified = list.filter((x) => (x.kyc_status || "pending") === "verified").length;
                    const plan = plans.find((p) => p.plan_id === editing.plan_id);
                    const allowed = Number(plan?.family_members) || 1;
                    return (
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-[#5C6B7A]" data-testid="admin-kyc-summary">
                          <b className="text-[#1F7A3D]">{verified}</b> of <b>{list.length}</b> KYC verified
                        </span>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-bold ${list.length > allowed ? "bg-[#E63946]/10 text-[#E63946]" : "bg-[#EAF1FA] text-[#1E4F87]"}`} data-testid="admin-member-slot-chip">
                          {list.length} / {allowed} slot{allowed > 1 ? "s" : ""}
                        </span>
                        {canEdit && list.length < allowed && (
                          <button onClick={beginAddMember} className="btn-primary !text-xs !px-3 !py-1.5" data-testid="edit-add-member-btn" disabled={!!draftMember}>+ Add Member</button>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="space-y-3">
                  {(() => {
                    // The primary's aadhaar/uhid/docs are stored on the membership doc
                    // top-level (not on members[primary]) — patch them in so the
                    // inline row reflects the truth instead of showing "Missing".
                    const list = (editing.members && editing.members.length)
                      ? editing.members.map((x) => (x.is_primary || x.member_id === editing.member_id)
                          ? { ...x,
                              aadhaar: x.aadhaar || editing.aadhaar || "",
                              uhid: x.uhid || editing.uhid || "",
                              aadhaar_doc_id: x.aadhaar_doc_id || editing.aadhaar_doc_id || null,
                              photo_doc_id: x.photo_doc_id || editing.photo_doc_id || null,
                              kyc_status: x.kyc_status || editing.kyc_status,
                              kyc_remarks: x.kyc_remarks || editing.kyc_remarks }
                          : x)
                      : [{ member_id: editing.member_id, is_primary: true,
                           full_name: editing.full_name, mobile: editing.mobile, dob: editing.dob,
                           aadhaar: editing.aadhaar, uhid: editing.uhid, relation: "self",
                           aadhaar_doc_id: editing.aadhaar_doc_id, photo_doc_id: editing.photo_doc_id,
                           kyc_status: editing.kyc_status, kyc_remarks: editing.kyc_remarks }];
                    return list.map((sm, idx) => (
                    <AdminMemberKycRow key={sm.member_id} sm={sm} idx={idx} editing={editing}
                        planTier={(plans.find((p) => p.plan_id === editing.plan_id) || {}).tier}
                        otherMembers={(editing.members || [])
                          .filter((x) => x.member_id !== sm.member_id && !x.is_primary)
                          .map((x) => ({ relation: x.relation, dob: x.dob, full_name: x.full_name }))}
                        canEditFull={canEditFull}
                        canEditUhid={canEditUhid}
                        /* Original values captured at modal-open — used for lock-once
                           decisions so the field doesn't disable after the first keystroke. */
                        originalAadhaar={editingOriginals?.members?.[sm.member_id]?.aadhaar || ""}
                        originalUhid={editingOriginals?.members?.[sm.member_id]?.uhid || ""}
                        onDocUpload={(file, key) => uploadDocForSub(idx, key, file)}
                        onAadhaarChange={(v) => setEditing((prev) => {
                          const arr = [...(prev.members || [])];
                          if (arr[idx]) arr[idx] = { ...arr[idx], aadhaar: v };
                          return { ...prev, members: arr.length ? arr : prev.members,
                                   aadhaar: sm.is_primary ? v : prev.aadhaar };
                        })}
                        onFieldChange={canEdit ? ((field, value) => setEditing((prev) => {
                          const arr = [...(prev.members || [])];
                          if (arr[idx]) arr[idx] = { ...arr[idx], [field]: value };
                          // Mirror primary's UHID + name/mobile/dob fields back to top-level
                          // so the Hospital section input stays in sync.
                          const patch = (sm.is_primary && ["full_name", "mobile", "dob", "uhid"].includes(field))
                            ? { [field]: value } : {};
                          return { ...prev, members: arr.length ? arr : prev.members, ...patch };
                        })) : undefined}
                        // Add Member: staff can do it. Remove: admin/super_admin only.
                        onRemove={(canEditFull && !sm.is_primary && sm.member_id !== editing.member_id)
                          ? (() => removeEditMember(sm)) : undefined}
                        onView={viewDoc}
                        onVerify={(status, remarks) => verifyKyc(sm.member_id, status, remarks)}
                      />
                  ));
                  })()}

                {/* Inline draft row — appears when the admin clicks "+ Add Member".
                    Same form fields & validation as the offline Step-3 row. */}
                {draftMember && editing && (() => {
                  const plan = plans.find((p) => p.plan_id === editing.plan_id);
                  const others = (editing.members || [])
                    .filter((x) => !x.is_primary)
                    .map((x) => ({ relation: x.relation, dob: x.dob, full_name: x.full_name }));
                  return (
                    <div className="rounded-2xl border border-dashed border-[#1E4F87]/50 bg-[#EAF1FA]/40 p-4" data-testid="edit-add-member-draft">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <h5 className="text-sm font-semibold text-[#0F2540]">New family member</h5>
                        <span className="text-[10px] uppercase tracking-wider text-[#5C6B7A]">Saved as soon as you click <b>Save</b>.</span>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <F label="Full name" required v={draftMember.full_name}
                           s={(v) => setDraftMember({ ...draftMember, full_name: v })}
                           testid="draft-full-name" />
                        <div>
                          <RelationSelect value={draftMember.relation}
                            onChange={(v) => setDraftMember({ ...draftMember, relation: v })}
                            excludeSelf required planTier={plan?.tier}
                            otherMembers={others}
                            testid="draft-relation" />
                        </div>
                        <F label="Mobile (optional)" v={draftMember.mobile}
                           s={(v) => setDraftMember({ ...draftMember, mobile: (v || "").replace(/\D/g, "").slice(0, 10) })}
                           testid="draft-mobile" />
                        <F label="Date of Birth" required type="date" v={draftMember.dob}
                           s={(v) => setDraftMember({ ...draftMember, dob: v })}
                           testid="draft-dob" />
                        <div className="sm:col-span-2">
                          <F label="Aadhaar (12-digit)" required v={draftMember.aadhaar}
                             s={(v) => setDraftMember({ ...draftMember, aadhaar: (v || "").replace(/\D/g, "").slice(0, 12) })}
                             testid="draft-aadhaar" />
                        </div>
                        <div className="sm:col-span-2">
                          <F label="UHID (Hospital ID — optional)" v={draftMember.uhid || ""}
                             s={(v) => setDraftMember({ ...draftMember, uhid: v })}
                             testid="draft-uhid" />
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2 justify-end">
                        <button onClick={cancelAddMember} className="btn-ghost !text-xs" data-testid="draft-cancel-btn">Cancel</button>
                        <button onClick={saveDraftMember} className="btn-primary !text-xs" data-testid="draft-save-btn">Save member</button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            {/* /scrollable body */}
            </div>

            {/* Sticky footer */}
            <div className="px-7 py-4 border-t border-[#E1E8F0] bg-white rounded-b-2xl flex items-center justify-between flex-wrap gap-2">
              <span className="text-[11px] text-[#5C6B7A]">
                {canEditFull
                  ? "Super Admin / Admin · full edit access"
                  : "Limited edit · mobile + address only on profile fields"}
              </span>
              <div className="flex gap-2">
                <button className="btn-ghost" onClick={closeEditing} disabled={busy} data-testid="cancel-member-profile-btn">Cancel</button>
                <button className="btn-primary" onClick={saveProfile} disabled={busy} data-testid="save-member-profile-btn">{busy ? "Saving…" : "Save changes"}</button>
              </div>
            </div>
            {/* /Profile body wrapper */}
            </div>
          </div>
        </div>
      )}

      {offline && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) toast("Click the × to exit — your form data is preserved."); }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-[1180px] max-h-[94vh] overflow-hidden flex flex-col shadow-2xl" data-testid="offline-enrol-modal">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b border-[#E1E8F0]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-2xl font-bold text-[#0F2540]">Offline Registration</h3>
                  <p className="text-xs text-[#5C6B7A] mt-1">Manually enrol a walk-in member · invoice + digital card auto-generated.</p>
                </div>
                <button onClick={() => !busy && (window.confirm("Discard registration? All entered data will be lost.") && setOffline(null))} className="text-[#5C6B7A] hover:text-[#E63946] text-xl leading-none px-2" aria-label="Close" data-testid="off-close">×</button>
              </div>
              {/* Stepper */}
              <div className="mt-5 flex items-center gap-2 sm:gap-3 overflow-x-auto pb-1">
                {[
                  { n: 1, label: "Plan & payment" },
                  { n: 2, label: "Primary member" },
                  { n: 3, label: "Family" },
                  { n: 4, label: "Review & confirm" },
                ].map((s, i) => {
                  const active = offline.step === s.n;
                  const done = offline.step > s.n;
                  return (
                    <React.Fragment key={s.n}>
                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full whitespace-nowrap text-xs font-semibold border ${active ? "bg-[#1E4F87] text-white border-[#1E4F87]" : done ? "bg-[#1F7A3D]/10 text-[#1F7A3D] border-[#1F7A3D]/30" : "bg-white text-[#5C6B7A] border-[#E1E8F0]"}`} data-testid={`off-step-${s.n}`}>
                        <span className={`h-5 w-5 rounded-full grid place-items-center text-[10px] ${active ? "bg-white text-[#1E4F87]" : done ? "bg-[#1F7A3D] text-white" : "bg-[#EAF1FA] text-[#5C6B7A]"}`}>{done ? "✓" : s.n}</span>
                        <span className="hidden sm:inline">{s.label}</span>
                      </div>
                      {i < 3 && <span className="flex-1 min-w-[12px] h-px bg-[#E1E8F0]"></span>}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 overflow-y-auto flex-1">
              {/* Step 1 — Plan & payment */}
              {offline.step === 1 && (
                <div className="space-y-5" data-testid="off-step1-body">
                  <div>
                    <label className="text-sm font-semibold text-[#0F2540]">Choose plan *</label>
                    <p className="text-xs text-[#5C6B7A] mt-0.5">Plan picks the member-cap and amount payable.</p>
                    <div className="mt-3 grid sm:grid-cols-3 gap-3">
                      {plans.filter((p) => p.is_active !== false).map((p) => {
                        const active = offline.plan_id === p.plan_id;
                        return (
                          <button key={p.plan_id} type="button" onClick={() => setOffPlan(p.plan_id)}
                            className={`text-left rounded-2xl border-2 p-4 transition ${active ? "border-[#1E4F87] bg-[#EAF1FA]/50 shadow-sm" : "border-[#E1E8F0] bg-white hover:border-[#C8D6E5]"}`}
                            data-testid={`off-plan-${p.tier}`}>
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-bold text-[#0F2540]">{p.name}</div>
                              {active && <span className="text-[10px] uppercase tracking-wider text-[#1E4F87] font-bold">Selected</span>}
                            </div>
                            <div className="mt-2 text-2xl font-display font-bold text-[#0F2540]">{inr(p.price)}</div>
                            <div className="mt-1 text-xs text-[#5C6B7A]">{p.duration_months}mo · up to {p.family_members} member{p.family_members > 1 ? "s" : ""}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-[#0F2540]">Payment method *</label>
                      <select value={offline.method} onChange={(e) => setOffline({ ...offline, method: e.target.value })}
                        className="mt-2 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5" data-testid="off-method">
                        <option value="cash">Cash</option>
                        <option value="cheque">Cheque</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="offline">Other (Offline)</option>
                      </select>
                    </div>
                    <F label="Amount override (optional)" type="number" v={offline.amount} s={(v) => setOffline({ ...offline, amount: v })} />
                  </div>
                </div>
              )}

              {/* Step 2 — Primary member */}
              {offline.step === 2 && (
                <div className="space-y-5" data-testid="off-step2-body">
                  <div className="rounded-xl bg-[#EAF1FA] border border-[#C8D6E5] p-3 text-xs text-[#1E4F87]">
                    Primary member of the membership — this person owns the dashboard login and receives the email.
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <F label="Full name" required v={offline.full_name} s={(v) => updateOff("full_name", v)} testid="off-full-name" />
                    <F label="Email (optional)" type="email" v={offline.email}
                       s={(v) => updateOff("email", v)}
                       onBlur={() => runOffDupCheck("email")}
                       err={offFieldErrors.email || (offDupErrors.email?.duplicate ? offDupErrors.email.message : "")}
                       testid="off-email" />
                    <F label="Mobile" required v={offline.mobile}
                       s={(v) => updateOff("mobile", v.replace(/\D/g, "").slice(0, 10))}
                       onBlur={() => runOffDupCheck("mobile")}
                       err={offFieldErrors.mobile || (offDupErrors.mobile?.duplicate ? offDupErrors.mobile.message : "")}
                       testid="off-mobile" />
                    <F label="Date of Birth" required type="date" v={offline.dob}
                       s={(v) => updateOff("dob", v)}
                       onBlur={() => runOffDupCheck("dob")}
                       err={offDupErrors.dob?.duplicate ? offDupErrors.dob.message : ""}
                       testid="off-dob" />
                    <div>
                      <label className="text-sm font-medium text-[#0F2540]">Gender<span className="text-[#E63946]"> *</span></label>
                      <select value={offline.gender} onChange={(e) => updateOff("gender", e.target.value)}
                        className={`mt-1 w-full rounded-xl border bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87] ${!offline.gender ? "border-[#E1E8F0]" : "border-[#E1E8F0]"}`} data-testid="off-gender">
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <F label="Aadhaar (12-digit)" required v={offline.aadhaar}
                       s={(v) => updateOff("aadhaar", v.replace(/\D/g, "").slice(0, 12))}
                       onBlur={() => runOffDupCheck("aadhaar")}
                       err={offFieldErrors.aadhaar || (offDupErrors.aadhaar?.duplicate ? offDupErrors.aadhaar.message : "")}
                       testid="off-aadhaar" />
                    <div className="sm:col-span-2">
                      <F label="Address" required v={offline.address} s={(v) => updateOff("address", v)} testid="off-address" />
                    </div>
                    <F label="City" required v={offline.city} s={(v) => updateOff("city", v)} testid="off-city" />
                    <F label="Pincode (6-digit)" required v={offline.pincode}
                       s={(v) => updateOff("pincode", v.replace(/\D/g, "").slice(0, 6))}
                       err={offFieldErrors.pincode}
                       testid="off-pincode" />
                    <StateSelect value={offline.state} onChange={(v) => updateOff("state", v)} label="State *" testid="off-state" />
                    <F label="GST Number (optional · B2B)" v={offline.gst_number} s={(v) => updateOff("gst_number", v)} testid="off-gst" />
                  </div>
                </div>
              )}

              {/* Step 3 — Family + confirm */}
              {offline.step === 3 && (
                <div className="space-y-4" data-testid="off-step3-body">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <h4 className="text-sm font-semibold text-[#0F2540]">Family members</h4>
                      <p className="text-xs text-[#5C6B7A]">Add additional members covered by this plan. Primary member is included automatically.</p>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full font-bold tracking-wide ${offUsedSlots > (offPlan?.family_members || 1) ? "bg-[#E63946]/10 text-[#E63946]" : "bg-[#1F7A3D]/10 text-[#1F7A3D]"}`} data-testid="off-slots-chip">
                      {offUsedSlots} / {offPlan?.family_members || 1} members
                    </span>
                  </div>

                  {offline.family_members.length === 0 && (
                    <div className="rounded-xl border-2 border-dashed border-[#E1E8F0] p-8 text-center text-sm text-[#5C6B7A]" data-testid="off-empty-family">
                      No family members added yet.
                      {offMaxFamily > 0 ? <> You can add up to <b>{offMaxFamily}</b> more.</> : <> This plan covers only the primary member.</>}
                    </div>
                  )}
                  <div className="space-y-3">
                    {offline.family_members.map((fm, i) => (
                      <div key={i} className="rounded-2xl border border-[#E1E8F0] p-4 bg-[#F4F7FB]/40" data-testid={`off-fam-row-${i}`}>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-bold uppercase tracking-wider text-[#A53A2A]">Member {i + 2}</span>
                          <button type="button" onClick={() => rmOffFam(i)} className="text-xs text-[#E63946] underline" data-testid={`off-rm-fam-${i}`}>Remove</button>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <F label="Full name *" v={fm.full_name} s={(v) => setOffFam(i, { full_name: v })} />
                          <RelationSelect value={fm.relation} onChange={(v) => setOffFam(i, { relation: v })} excludeSelf testid={`off-fam-relation-${i}`} required
                            planTier={offPlan?.tier}
                            otherMembers={offline.family_members.filter((_, j) => j !== i)} />
                          <F label="Mobile (optional)" v={fm.mobile} s={(v) => setOffFam(i, { mobile: v.replace(/\D/g, "").slice(0, 10) })} />
                          <F label="Date of Birth *" type="date" v={fm.dob} s={(v) => setOffFam(i, { dob: v })} />
                          <div className="sm:col-span-2">
                            <F label="Aadhaar (12-digit) *" v={fm.aadhaar} s={(v) => setOffFam(i, { aadhaar: (v || "").replace(/\D/g, "").slice(0, 12) })} testid={`off-fam-aadhaar-${i}`} />
                          </div>
                          <div className="sm:col-span-2">
                            <F label="UHID (Hospital ID — optional)" v={fm.uhid || ""} s={(v) => setOffFam(i, { uhid: v })} testid={`off-fam-uhid-${i}`} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={addOffFam} disabled={!offCanAdd}
                    className={`w-full rounded-2xl border-2 border-dashed py-3 text-sm font-semibold transition ${offCanAdd ? "border-[#1E4F87] text-[#1E4F87] hover:bg-[#EAF1FA]" : "border-[#E1E8F0] text-[#5C6B7A] cursor-not-allowed"}`}
                    data-testid="off-add-fam">
                    {offCanAdd ? "+ Add family member" : `Member limit reached for ${offPlan?.name || "this plan"}`}
                  </button>
                </div>
              )}

              {/* Step 4 — Review & confirm */}
              {offline.step === 4 && (
                <div className="space-y-5" data-testid="off-step4-body">
                  <div className="rounded-xl bg-[#EAF1FA] border border-[#C8D6E5] p-3 text-xs text-[#1E4F87]">
                    Review the details below before generating the membership card and recording payment.
                  </div>

                  {/* Plan + payment */}
                  <div className="rounded-2xl border border-[#E1E8F0] bg-white p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <h5 className="text-sm font-bold text-[#0F2540]">Plan & payment</h5>
                      <button onClick={() => setOffline({ ...offline, step: 1 })} className="text-[10px] uppercase tracking-wider text-[#1E4F87] underline">Edit</button>
                    </div>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2 text-sm text-[#0F2540]">
                      <div><span className="text-[#5C6B7A]">Plan: </span><b>{offPlan?.name}</b></div>
                      <div><span className="text-[#5C6B7A]">Payment method: </span><b className="capitalize">{(offline.method || "cash").replace("_", " ")}</b></div>
                      <div><span className="text-[#5C6B7A]">Family slots: </span>{offUsedSlots} / {offPlan?.family_members || 1}</div>
                      {offline.amount && <div><span className="text-[#5C6B7A]">Manual amount: </span><b>{inr(Number(offline.amount))}</b></div>}
                    </div>
                  </div>

                  {/* Primary member */}
                  <div className="rounded-2xl border border-[#E1E8F0] bg-white p-4">
                    <div className="flex items-center justify-between">
                      <h5 className="text-sm font-bold text-[#0F2540]">Primary member</h5>
                      <button onClick={() => setOffline({ ...offline, step: 2 })} className="text-[10px] uppercase tracking-wider text-[#1E4F87] underline">Edit</button>
                    </div>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2 text-sm">
                      <div><span className="text-[#5C6B7A]">Name: </span><b>{offline.full_name}</b></div>
                      <div><span className="text-[#5C6B7A]">DOB: </span>{offline.dob || "—"}</div>
                      <div><span className="text-[#5C6B7A]">Email: </span>{offline.email || <span className="italic text-[#5C6B7A]">not provided</span>}</div>
                      <div><span className="text-[#5C6B7A]">Mobile: </span>{offline.mobile}</div>
                      <div><span className="text-[#5C6B7A]">Gender: </span>{offline.gender || "—"}</div>
                      <div><span className="text-[#5C6B7A]">Aadhaar: </span><span className="font-mono">{offline.aadhaar || "—"}</span></div>
                      <div className="sm:col-span-2"><span className="text-[#5C6B7A]">Address: </span>{offline.address}, {offline.city} — {offline.pincode}, {offline.state}</div>
                      {offline.gst_number && <div className="sm:col-span-2"><span className="text-[#5C6B7A]">GSTIN: </span><span className="font-mono">{offline.gst_number}</span></div>}
                    </div>
                  </div>

                  {/* Family */}
                  {(offline.family_members || []).length > 0 && (
                    <div className="rounded-2xl border border-[#E1E8F0] bg-white p-4">
                      <div className="flex items-center justify-between">
                        <h5 className="text-sm font-bold text-[#0F2540]">Family members ({offline.family_members.length})</h5>
                        <button onClick={() => setOffline({ ...offline, step: 3 })} className="text-[10px] uppercase tracking-wider text-[#1E4F87] underline">Edit</button>
                      </div>
                      <div className="mt-2 grid sm:grid-cols-2 gap-2">
                        {offline.family_members.map((fm, i) => (
                          <div key={i} className="rounded-lg bg-[#F4F7FB] p-2 text-xs">
                            <div className="font-semibold text-[#0F2540]">{fm.full_name} <span className="text-[#5C6B7A]">· {fm.relation}</span></div>
                            <div className="text-[#5C6B7A]">DOB {fm.dob} · {fm.mobile || "no mobile"} · Aadhaar <span className="font-mono">{fm.aadhaar}</span></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Discount code Validate */}
                  <div className="rounded-2xl border border-[#E1E8F0] bg-white p-4">
                    <h5 className="text-sm font-bold text-[#0F2540]">Discount code</h5>
                    <div className="mt-2 flex gap-2 items-start flex-wrap">
                      <input value={offline.referral_code || ""}
                        onChange={(e) => updateOff("referral_code", e.target.value.toUpperCase().trim())}
                        placeholder="EMP-XXXXX or member code"
                        className="flex-1 min-w-[180px] rounded-xl border border-[#E1E8F0] bg-white px-3 py-2 text-sm uppercase font-mono"
                        data-testid="off-review-discount" />
                      <button onClick={validateOffDiscount} disabled={offRefBusy || !offline.referral_code}
                        className="btn-primary !text-xs !px-4" data-testid="off-validate-discount">
                        {offRefBusy ? "Validating…" : "Validate"}
                      </button>
                    </div>
                    {offRefStatus && (
                      <div className={`mt-2 text-xs ${offRefStatus.valid ? "text-[#1F7A3D]" : "text-[#E63946]"}`} data-testid="off-discount-status">
                        {offRefStatus.valid
                          ? `✓ Valid · ${offRefStatus.member_name || offRefStatus.employee_name || offRefStatus.owner || ""} · ${Number(offRefStatus.discount_percent) || 0}% off`
                          : `✗ ${offRefStatus.message || "Invalid code"}`}
                      </div>
                    )}
                  </div>

                  {/* Pricing breakdown */}
                  <div className="rounded-2xl border-2 border-[#1E4F87]/30 bg-[#F4F7FB] p-4" data-testid="off-pricing-breakdown">
                    <h5 className="text-sm font-bold text-[#0F2540]">Pricing & GST breakdown</h5>
                    {offPricing ? (
                      <div className="mt-2 space-y-1.5 text-sm">
                        <PriceRow label="Plan price" value={inr(offPricing.base_price)} />
                        {offPricing.discount > 0 && <PriceRow label={`Discount (${Number(offRefStatus?.discount_percent) || 0}%)`} value={`− ${inr(offPricing.discount)}`} accent="text-[#1F7A3D]" />}
                        <PriceRow label="Taxable" value={inr(offPricing.taxable)} />
                        <PriceRow label={`GST (${offPricing.gst_percent}%)`} value={inr(offPricing.gst)} />
                        <div className="border-t border-[#1E4F87]/20 pt-2 mt-2 flex items-center justify-between">
                          <span className="text-sm font-bold text-[#0F2540]">Final payable</span>
                          <span className="text-xl font-bold text-[#1E4F87]" data-testid="off-final-amount">{inr(offPricing.final)}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-[#5C6B7A] mt-2">Calculating pricing…</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-[#E1E8F0] bg-[#F4F7FB]/40 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-[#5C6B7A]">
                {offPlan && <>Plan total: <b className="text-[#0F2540]">{inr(offline.amount ? Number(offline.amount) : offPlan.price)}</b></>}
              </div>
              <div className="flex gap-2">
                {offline.step > 1 && (
                  <button className="btn-ghost" onClick={offBack} disabled={busy} data-testid="off-back-btn">Back</button>
                )}
                {offline.step < 4 ? (
                  <button className="btn-primary" onClick={offNext} disabled={busy} data-testid="off-next-btn">Next →</button>
                ) : (
                  <button className="btn-primary" onClick={submitOffline} disabled={busy} data-testid="off-submit-btn">{busy ? "Registering…" : "Register & Generate Card"}</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* #4 — Extension modal with editable days + mandatory reason + history */}
      {extendModal && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setExtendModal(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="extend-modal">
            <h3 className="font-display text-xl font-bold text-[#0F2540]">Extend membership</h3>
            <p className="text-xs text-[#5C6B7A] mt-1">
              {extendModal.membership.full_name} · {extendModal.membership.member_id} ·
              currently valid till {new Date(extendModal.membership.valid_till).toLocaleDateString("en-IN")}
            </p>
            <label className="block mt-4 text-sm font-medium text-[#0F2540]">Number of days *</label>
            <input
              type="number"
              min={1}
              step={1}
              value={extendModal.days}
              onChange={(e) => setExtendModal({ ...extendModal, days: Math.max(1, parseInt(e.target.value || 1, 10)) })}
              className="mt-1 w-full rounded-xl border border-[#E1E8F0] px-3 py-2 text-sm"
              data-testid="extend-days-input"
            />
            <p className="text-[11px] text-[#5C6B7A] mt-1">Enter any positive number of days (e.g. 15, 30, 60, 90, 180).</p>
            <label className="block mt-4 text-sm font-medium text-[#0F2540]">Reason for extension *</label>
            <textarea
              value={extendReason}
              onChange={(e) => setExtendReason(e.target.value)}
              placeholder="e.g. goodwill compensation, manual top-up, post-pay reconciliation, etc."
              rows={3}
              className="mt-1 w-full rounded-xl border border-[#E1E8F0] px-3 py-2 text-sm"
              data-testid="extend-reason-input"
            />
            {extendHistory.length > 0 && (
              <div className="mt-5">
                <h4 className="text-xs font-bold uppercase tracking-wider text-[#5C6B7A]">Past extensions ({extendHistory.length})</h4>
                <ul className="mt-2 space-y-1.5 max-h-44 overflow-y-auto">
                  {extendHistory.map((h) => (
                    <li key={h.ext_id} className="text-xs rounded-lg bg-[#F4F7FB] p-2.5" data-testid={`ext-history-${h.ext_id}`}>
                      <div className="flex justify-between text-[#0F2540] font-semibold">
                        <span>+{h.days}d</span>
                        <span>{new Date(h.created_at).toLocaleDateString("en-IN")}</span>
                      </div>
                      <div className="text-[#5C6B7A] mt-0.5">{h.reason}</div>
                      <div className="text-[10px] text-[#5C6B7A] mt-0.5">by {h.actor_email}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setExtendModal(null)} data-testid="extend-cancel-btn">Cancel</button>
              <button className="btn-primary" onClick={confirmExtend} data-testid="extend-confirm-btn">Confirm extension</button>
            </div>
          </div>
        </div>
      )}
      {cardFor && (
        <MembershipCardModal membership={cardFor} onClose={() => setCardFor(null)} />
      )}
    </div>
  );
}

function DocPreview({ label, docId, onView }) {
  if (!docId) return (
    <div className="rounded-xl border border-dashed border-[#E1E8F0] p-3 text-sm text-[#5C6B7A]">{label}: not uploaded</div>
  );
  return (
    <div className="rounded-xl border border-[#E1E8F0] p-3 flex items-center justify-between gap-3">
      <div>
        <div className="text-sm font-medium text-[#0F2540]">{label}</div>
        <div className="text-xs text-[#5C6B7A] font-mono truncate">{docId}</div>
      </div>
      <button onClick={() => onView(docId)} className="text-sm text-[#1E4F87] underline">View</button>
    </div>
  );
}

function DocEditor({ label, docId, onView, onUpload, accept = "image/*,application/pdf" }) {
  const inputRef = React.useRef(null);
  return (
    <div className="rounded-xl border border-[#E1E8F0] p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-[#0F2540]">{label}</div>
          <div className={`text-xs truncate ${docId ? "text-[#1F7A3D] font-semibold" : "text-[#5C6B7A]"}`}>
            {docId ? "Uploaded" : "Not uploaded"}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {docId && <button type="button" onClick={() => onView(docId)} className="text-xs text-[#1E4F87] underline">View</button>}
          <button type="button" onClick={() => inputRef.current?.click()} className="text-xs text-[#A53A2A] underline" data-testid={`upload-${label.toLowerCase()}-btn`}>
            {docId ? "Replace" : "Upload"}
          </button>
        </div>
      </div>
      <input ref={inputRef} type="file" accept={accept} className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
    </div>
  );
}

function AdminMemberKycRow({ sm, idx, editing, onDocUpload, onAadhaarChange, onFieldChange, onRemove, onView, onVerify, planTier, otherMembers, canEditFull = true, canEditUhid = true, originalAadhaar = "", originalUhid = "" }) {
  const [remarks, setRemarks] = React.useState(sm.kyc_remarks || "");
  const [showRemarks, setShowRemarks] = React.useState(false);
  const [showEdit, setShowEdit] = React.useState(false);
  // Lock-once-entered: reference the ORIGINAL DB value (frozen at modal open),
  // not the live in-progress state. Otherwise typing the first digit locks the
  // field instantly. Super_admin/admin always editable.
  const aadhaarLocked = !!originalAadhaar && !canEditFull;
  const uhidLocked = !!originalUhid && !canEditUhid;
  const status = (sm.kyc_status || "pending").toLowerCase();
  const relLabel = sm.is_primary ? "Primary" : (sm.relation || "Member");
  const aadhaarStatus = sm.aadhaar ? "Provided" : "Missing";
  const aadhaarDocStatus = sm.aadhaar_doc_id ? "Uploaded" : "Missing";
  const photoStatus = sm.photo_doc_id ? "Uploaded" : "Missing";
  const tone = {
    verified: "bg-[#1F7A3D]/10 text-[#1F7A3D] border-[#1F7A3D]/20",
    rejected: "bg-[#A53A2A]/10 text-[#A53A2A] border-[#A53A2A]/20",
    pending:  "bg-[#C89B3C]/15 text-[#7A5A1F] border-[#C89B3C]/30",
  }[status] || "bg-[#F4F7FB] text-[#5C6B7A] border-[#E1E8F0]";

  return (
    <div
      className={`relative rounded-xl border p-3 ${sm.is_primary
        ? "border-[#1E4F87]/30 bg-gradient-to-br from-[#EAF1FA]/80 to-[#F8FAFD] shadow-[inset_4px_0_0_0_#1E4F87]"
        : "border-[#E1E8F0] bg-[#F8FAFD]"
      }`}
      data-testid={`admin-member-kyc-${sm.member_id}`}
    >
      {/* Header row — identity + KYC badge */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-semibold text-[#0F2540]">{sm.full_name || "—"}</div>
            <span className="font-mono text-[11px] text-[#5C6B7A]">{sm.member_id}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold ${
              sm.is_primary
                ? "bg-gradient-to-r from-[#1E4F87] to-[#0F2540] text-white shadow-sm"
                : "bg-[#F4F7FB] text-[#5C6B7A]"
            }`}>{sm.is_primary ? "★ Primary" : relLabel}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold border ${tone}`}
              data-testid={`admin-kyc-badge-${sm.member_id}`}>
              {status === "verified" ? "KYC Verified" : status === "rejected" ? "KYC Rejected" : "KYC Pending"}
            </span>
          </div>
          <div className="mt-1 text-[11px] text-[#5C6B7A]">
            DOB {sm.dob || "—"} · {sm.mobile || (sm.is_primary ? "—" : "no mobile")}
            {sm.uhid && <> · UHID <span className="font-mono text-[#A0741F]">{sm.uhid}</span></>}
          </div>
        </div>
        {(onFieldChange || onRemove) && (
          <div className="flex items-center gap-2">
            {onFieldChange && (
              <button onClick={() => setShowEdit((v) => !v)}
                className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-md bg-white border border-[#1E4F87]/30 text-[#1E4F87] hover:bg-[#EAF1FA] font-bold"
                data-testid={`row-edit-toggle-${sm.member_id}`}>
                {showEdit ? "Hide edit" : "Edit details"}
              </button>
            )}
            {onRemove && !sm.is_primary && (
              <button onClick={onRemove}
                className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-md bg-[#E63946]/10 text-[#E63946] hover:bg-[#E63946]/20 font-bold"
                data-testid={`edit-rm-member-${sm.member_id}`}>
                Remove
              </button>
            )}
          </div>
        )}
      </div>

      {showEdit && onFieldChange && (
        <div className="mt-3 rounded-lg bg-white border border-[#1E4F87]/20 p-3 grid sm:grid-cols-2 gap-3" data-testid={`row-edit-panel-${sm.member_id}`}>
          <div>
            <label className="text-[11px] text-[#5C6B7A]">Full name{!canEditFull && <span className="text-[#C89B3C] ml-1">(locked)</span>}</label>
            <input value={sm.full_name || ""} disabled={!canEditFull} onChange={(e) => onFieldChange("full_name", e.target.value)}
              className={`mt-1 w-full rounded-md border border-[#E1E8F0] px-2 py-1.5 text-xs outline-none focus:border-[#1E4F87] ${canEditFull ? "bg-white" : "bg-[#F4F7FB] text-[#5C6B7A]"}`}
              data-testid={`row-full-name-${sm.member_id}`} />
          </div>
          {/* Relation is locked for the primary member — keep "self" intact. */}
          {sm.is_primary ? (
            <div>
              <label className="text-[11px] text-[#5C6B7A]">Relation</label>
              <input value="Self" disabled
                className="mt-1 w-full rounded-md border border-[#E1E8F0] bg-[#F4F7FB] px-2 py-1.5 text-xs text-[#5C6B7A]" />
            </div>
          ) : canEditFull ? (
            <RelationSelect value={sm.relation || ""}
              onChange={(v) => onFieldChange("relation", v)}
              excludeSelf required planTier={planTier} otherMembers={otherMembers || []}
              testid={`row-relation-${sm.member_id}`} />
          ) : (
            <div>
              <label className="text-[11px] text-[#5C6B7A]">Relation <span className="text-[#C89B3C]">(locked)</span></label>
              <input value={sm.relation || ""} disabled
                className="mt-1 w-full rounded-md border border-[#E1E8F0] bg-[#F4F7FB] px-2 py-1.5 text-xs text-[#5C6B7A]" />
            </div>
          )}
          <div>
            <label className="text-[11px] text-[#5C6B7A]">Mobile {sm.is_primary ? "" : "(optional)"}</label>
            <input value={sm.mobile || ""} onChange={(e) => onFieldChange("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="10-digit"
              inputMode="numeric"
              className="mt-1 w-full rounded-md border border-[#E1E8F0] bg-white px-2 py-1.5 text-xs outline-none focus:border-[#1E4F87]"
              data-testid={`row-mobile-${sm.member_id}`} />
          </div>
          <div>
            <label className="text-[11px] text-[#5C6B7A]">Date of birth{!canEditFull && <span className="text-[#C89B3C] ml-1">(locked)</span>}</label>
            <input type="date" value={sm.dob || ""} disabled={!canEditFull} onChange={(e) => onFieldChange("dob", e.target.value)}
              className={`mt-1 w-full rounded-md border border-[#E1E8F0] px-2 py-1.5 text-xs outline-none focus:border-[#1E4F87] ${canEditFull ? "bg-white" : "bg-[#F4F7FB] text-[#5C6B7A]"}`}
              data-testid={`row-dob-${sm.member_id}`} />
          </div>
          <p className="sm:col-span-2 text-[10px] text-[#5C6B7A]">
            Changes save when you click <b>Save</b> at the bottom of the modal. Plan-composition rules will be re-checked.
          </p>
        </div>
      )}

      {/* Operational status grid */}
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
        <Stat label="Aadhaar #" value={aadhaarStatus} ok={!!sm.aadhaar} />
        <Stat label="Aadhaar doc" value={aadhaarDocStatus} ok={!!sm.aadhaar_doc_id} />
        <Stat label="Photo doc" value={photoStatus} ok={!!sm.photo_doc_id} />
        <Stat label="Membership" value={editing.membership_number || editing.membership_id?.slice(0, 10) || "—"} mono />
      </div>

      {/* Aadhaar number inline edit — lock-once-entered for staff/accounts */}
      <div className="mt-2 flex items-center gap-2">
        <label className="text-[11px] text-[#5C6B7A] w-20">Aadhaar #{aadhaarLocked ? " 🔒" : ""}</label>
        <input
          value={sm.aadhaar || ""}
          disabled={aadhaarLocked}
          onChange={(e) => onAadhaarChange(e.target.value.replace(/\D/g, "").slice(0, 12))}
          placeholder={aadhaarLocked ? "Locked — contact Admin" : "12-digit"}
          inputMode="numeric"
          className={`flex-1 rounded-md border border-[#E1E8F0] px-2 py-1 text-xs font-mono outline-none focus:border-[#1E4F87] ${aadhaarLocked ? "bg-[#F4F7FB] text-[#5C6B7A] cursor-not-allowed" : "bg-white"}`}
          data-testid={`admin-aadhaar-no-${sm.member_id}`}
        />
      </div>

      {/* UHID — lock-once-entered for staff/accounts; super_admin/admin always editable.
          For the primary, this input is the same as the top-level "Primary member UHID"
          field — they mirror each other. */}
      {onFieldChange && (
        <div className="mt-2 flex items-center gap-2">
          <label className="text-[11px] text-[#5C6B7A] w-20">UHID{uhidLocked ? " 🔒" : ""}</label>
          <input
            value={sm.uhid || ""}
            disabled={uhidLocked}
            onChange={(e) => onFieldChange("uhid", e.target.value)}
            placeholder={uhidLocked ? "Locked — contact Admin" : (sm.is_primary ? "Primary member UHID (unique)" : "Member UHID (unique)")}
            className={`flex-1 rounded-md border border-[#E1E8F0] px-2 py-1 text-xs font-mono outline-none focus:border-[#1E4F87] ${uhidLocked ? "bg-[#F4F7FB] text-[#5C6B7A] cursor-not-allowed" : "bg-white"}`}
            data-testid={`admin-uhid-${sm.member_id}`}
          />
        </div>
      )}

      {/* Doc uploaders */}
      <div className="mt-2 grid sm:grid-cols-2 gap-2">
        <DocEditor label="Aadhaar" docId={sm.aadhaar_doc_id} onView={onView}
          onUpload={(f) => onDocUpload(f, "aadhaar_doc_id")} />
        <DocEditor label="Photo" accept="image/*" docId={sm.photo_doc_id} onView={onView}
          onUpload={(f) => onDocUpload(f, "photo_doc_id")} />
      </div>

      {/* KYC verify actions */}
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap pt-3 border-t border-[#E1E8F0]">
        <div className="text-[11px] text-[#5C6B7A]">
          {sm.kyc_verified_at && (
            <>Last action: <b>{status}</b> by <b className="text-[#0F2540]">{sm.kyc_verified_by_username || sm.kyc_verified_by_email || "—"}</b> on {new Date(sm.kyc_verified_at).toLocaleString("en-IN")}</>
          )}
          {!sm.kyc_verified_at && <>Awaiting first verification</>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowRemarks((s) => !s)}
            className="text-[11px] underline text-[#1E4F87]"
            data-testid={`toggle-remarks-${sm.member_id}`}>
            {showRemarks ? "Hide remarks" : "Add remarks"}
          </button>
          {/* Verify button — disabled only when prerequisites are missing.
              Prerequisites (Feb 2026 — matches backend KYC guard):
                1. Aadhaar document uploaded
                2. Photo uploaded
                3. Hospital UHID linked
              When the user clicks while disabled, we surface a single toast
              listing the exact missing items so they don't have to guess. */}
          {(() => {
            const missing = [];
            if (!sm.aadhaar_doc_id) missing.push("Aadhaar document");
            if (!sm.photo_doc_id) missing.push("Photo");
            // Primary's UHID lives in two places (top-level + sub-member). For
            // family members it's only on the sub-member. Check both.
            const subUhid = (sm.uhid || "").trim();
            const topUhid = sm.is_primary ? (editing?.uhid || "").trim() : "";
            if (!subUhid && !topUhid) missing.push("Hospital UHID");
            const isVerified = status === "verified";
            const isBlocked = missing.length > 0;
            const onClickHandler = () => {
              if (isBlocked) {
                toast.error(`Cannot verify KYC — pending: ${missing.join(", ")}.`);
                return;
              }
              onVerify("verified", remarks);
            };
            return (
              <button
                onClick={onClickHandler}
                disabled={isVerified}
                title={
                  isVerified
                    ? "Already verified"
                    : isBlocked
                    ? `Pending: ${missing.join(", ")}`
                    : "Verify KYC"
                }
                className={`text-[11px] px-2.5 py-1 rounded-md font-semibold transition ${
                  isVerified
                    ? "bg-[#1F7A3D]/30 text-white cursor-not-allowed"
                    : isBlocked
                    ? "bg-[#C89B3C]/60 text-white hover:bg-[#C89B3C]/80 cursor-help"
                    : "bg-[#1F7A3D] text-white hover:bg-[#155026]"
                }`}
                data-testid={`kyc-verify-${sm.member_id}`}
              >
                ✓ Verify
              </button>
            );
          })()}
          <button
            onClick={() => onVerify("rejected", remarks || "Documents do not match")}
            disabled={status === "rejected"}
            className="text-[11px] px-2.5 py-1 rounded-md bg-[#A53A2A] text-white font-semibold disabled:opacity-40"
            data-testid={`kyc-reject-${sm.member_id}`}>
            ✕ Reject
          </button>
          {status !== "pending" && (
            <button onClick={() => onVerify("pending", "")}
              className="text-[11px] px-2.5 py-1 rounded-md bg-white border border-[#E1E8F0] text-[#5C6B7A] font-semibold"
              data-testid={`kyc-reset-${sm.member_id}`}>
              Reset to Pending
            </button>
          )}
        </div>
      </div>
      {(showRemarks || sm.kyc_remarks) && (
        <textarea
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Add remarks (e.g. document blurry, name mismatch)…"
          rows={2}
          className="mt-2 w-full rounded-md border border-[#E1E8F0] bg-white px-2 py-1.5 text-xs outline-none focus:border-[#1E4F87]"
          data-testid={`kyc-remarks-${sm.member_id}`}
        />
      )}
    </div>
  );
}

function Stat({ label, value, ok, mono }) {
  return (
    <div className="rounded-md bg-white border border-[#E1E8F0] px-2 py-1">
      <div className="text-[9px] uppercase tracking-wider text-[#5C6B7A]">{label}</div>
      <div className={`text-[11px] font-semibold ${mono ? "font-mono" : ""} ${
        ok === undefined ? "text-[#0F2540]" : ok ? "text-[#1F7A3D]" : "text-[#A53A2A]"
      }`}>{value}</div>
    </div>
  );
}

export function AdminPayments() {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [plans, setPlansList] = useState([]);
  const [employees, setEmployeesList] = useState([]);
  const [filter, setFilter] = useState({ period: "", from_date: "", to_date: "" });
  const [extras, setExtras] = useState({ method: "", status: "", plan_id: "", employee_code: "", q_search: "" });
  const [dedupeBusy, setDedupeBusy] = useState(false);
  useEffect(() => {
    api.get("/plans?all=true").then((r) => setPlansList(r.data)).catch(() => {});
    api.get("/employees").then((r) => setEmployeesList(Array.isArray(r.data) ? r.data : r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const params = { ...filter, ...extras, page, limit };
    Object.keys(params).forEach((k) => { if (params[k] === "" || params[k] == null) delete params[k]; });
    api.get("/payments", { params }).then((r) => { setPayments(r.data.items); setTotal(r.data.total); });
  }, [filter, extras, page, limit]);
  useEffect(() => { setPage(1); }, [filter, extras]);

  const reload = () => {
    const params = { ...filter, ...extras, page, limit };
    Object.keys(params).forEach((k) => { if (params[k] === "" || params[k] == null) delete params[k]; });
    return api.get("/payments", { params }).then((r) => { setPayments(r.data.items); setTotal(r.data.total); });
  };

  // Detect & clean duplicate payment rows (same payment_id recorded twice — see
  // /api/admin/payments/dedupe in server.py). Two-step: dry-run preview →
  // confirm → live delete. Super-admin only.
  const dedupe = async () => {
    if (dedupeBusy) return;
    setDedupeBusy(true);
    try {
      const dry = await api.post("/admin/payments/dedupe?dry_run=true");
      const groups = dry.data.duplicate_groups || 0;
      const toDelete = dry.data.rows_marked_for_delete || 0;
      if (!toDelete) {
        toast.success("No duplicates found — payments are clean.");
        return;
      }
      const ok = window.confirm(
        `Found ${groups} duplicate payment group(s) — ${toDelete} extra row(s) will be removed.\n\n` +
        `For each duplicate, the canonical row (with Razorpay signature) is kept. ` +
        `Proceed?`,
      );
      if (!ok) return;
      const live = await api.post("/admin/payments/dedupe?dry_run=false");
      toast.success(`Removed ${live.data.rows_deleted} duplicate row(s).`);
      await reload();
    } catch (e) {
      toast.error(apiErr(e, "Dedupe failed"));
    } finally {
      setDedupeBusy(false);
    }
  };

  const exportCsv = () => {
    const rows = [
      ["Payment ID", "Order", "Member", "Amount", "Method", "Status", "Date"],
      ...payments.map((p) => [p.payment_id, p.order_id, p.member_id, p.amount, p.method, p.status, p.created_at]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = `payments-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-payments">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold text-[#0F2540]">Payments</h1>
        <div className="flex flex-wrap items-center gap-2">
          <DateFilter value={filter} onChange={setFilter} testid="payments-filter" />
          {user?.role === "super_admin" && (
            <button onClick={dedupe} disabled={dedupeBusy} className="btn-ghost text-sm disabled:opacity-50" data-testid="payments-dedupe-btn" title="Detect & remove duplicate payment rows">
              {dedupeBusy ? "Checking…" : "Dedupe"}
            </button>
          )}
          <button onClick={exportCsv} className="btn-ghost text-sm" data-testid="payments-export-csv">Export CSV</button>
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-white border border-[#E1E8F0] p-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div>
          <label className="text-xs text-[#5C6B7A]">Method</label>
          <select value={extras.method} onChange={(e) => setExtras({ ...extras, method: e.target.value })}
            className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2 text-sm"
            data-testid="payments-filter-method">
            <option value="">All</option>
            <option value="razorpay">Razorpay (online)</option>
            <option value="cash">Cash</option>
            <option value="cheque">Cheque</option>
            <option value="bank_transfer">Bank transfer</option>
            <option value="corporate">Corporate bulk</option>
            <option value="offline">Offline</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-[#5C6B7A]">Status</label>
          <select value={extras.status} onChange={(e) => setExtras({ ...extras, status: e.target.value })}
            className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2 text-sm"
            data-testid="payments-filter-status">
            <option value="">All</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-[#5C6B7A]">Plan</label>
          <select value={extras.plan_id} onChange={(e) => setExtras({ ...extras, plan_id: e.target.value })}
            className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2 text-sm"
            data-testid="payments-filter-plan">
            <option value="">All plans</option>
            {plans.map((p) => <option key={p.plan_id} value={p.plan_id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-[#5C6B7A]">Employee (referral)</label>
          <select value={extras.employee_code} onChange={(e) => setExtras({ ...extras, employee_code: e.target.value })}
            className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2 text-sm"
            data-testid="payments-filter-employee">
            <option value="">All employees</option>
            {employees.map((e) => <option key={e.employee_id} value={e.referral_code}>{e.name} · {e.referral_code}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-[#5C6B7A]">Search (name / email / id)</label>
          <input value={extras.q_search} onChange={(e) => setExtras({ ...extras, q_search: e.target.value })}
            className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2 text-sm"
            placeholder="John / NPC0001 / 9876…"
            data-testid="payments-filter-search" />
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-white border border-[#E1E8F0] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EAF1FA] text-[#5C6B7A]">
            <tr>
              <th className="text-left px-4 py-3">Payment ID</th><th className="text-left px-4 py-3">Order</th>
              <th className="text-left px-4 py-3">Member</th><th className="text-left px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Method</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.payment_id} className="border-t border-[#E1E8F0]">
                <td className="px-4 py-3 font-mono text-xs">{p.payment_id}</td>
                <td className="px-4 py-3 font-mono text-xs">{p.order_id}</td>
                <td className="px-4 py-3 font-mono text-xs">{p.member_id}</td>
                <td className="px-4 py-3">{inr(p.amount)}</td>
                <td className="px-4 py-3">{p.method}</td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-full bg-[#1F7A3D]/10 text-[#1F7A3D]">{p.status}</span></td>
                <td className="px-4 py-3 text-[#5C6B7A]">{new Date(p.created_at).toLocaleString("en-IN")}</td>
              </tr>
            ))}
            {payments.length === 0 && <tr><td colSpan="7" className="px-4 py-6 text-center text-[#5C6B7A]">No payments yet.</td></tr>}
          </tbody>
        </table>
        <Pagination page={page} limit={limit} total={total} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} testid="payments-pagination" />
      </div>
    </div>
  );
}

function F({ label, v, s, type = "text", testid, onBlur, err, required, placeholder, disabled }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}{required && <span className="text-[#E63946]"> *</span>}</label>
      <input type={type} value={v ?? ""} onChange={(e) => s(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testid}
        className={`mt-1 w-full rounded-xl border px-4 py-2.5 outline-none focus:border-[#1E4F87] ${
          disabled ? "bg-[#F4F7FB] text-[#5C6B7A] cursor-not-allowed" : "bg-white"
        } ${err ? "border-[#E63946]" : "border-[#E1E8F0]"}`} />
      {err && <p className="text-xs text-[#E63946] mt-1" data-testid={testid ? `${testid}-err` : undefined}>{err}</p>}
    </div>
  );
}

function PriceRow({ label, value, accent = "text-[#0F2540]" }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-[#5C6B7A]">{label}</span>
      <span className={`font-semibold ${accent}`}>{value}</span>
    </div>
  );
}

// =========================================================
// NPC ADMIN — Renewals, Corporate, Employees, Referrals, GST
// =========================================================

export function AdminRenewals() {
  const [data, setData] = useState({ due_30: [], due_15: [], due_7: [], expired: [] });
  const load = () => api.get("/renewals").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const extend = async (id) => {
    const reason = window.prompt("Reason for renewing this membership (+365 days):");
    if (!reason || !reason.trim()) {
      toast.error("Reason is required for renewal extensions");
      return;
    }
    try {
      await api.put(`/memberships/${id}`, { extend_days: 365, reason: reason.trim() });
      toast.success("Renewed +365 days");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const buckets = [
    ["Due in 7 days", data.due_7, "text-[#E63946]"],
    ["Due in 15 days", data.due_15, "text-[#A53A2A]"],
    ["Due in 30 days", data.due_30, "text-[#C89B3C]"],
    ["Expired", data.expired, "text-[#5C6B7A]"],
  ];
  return (
    <div data-testid="admin-renewals">
      <h1 className="font-display text-3xl font-bold text-[#0F2540]">Renewals</h1>
      <p className="text-[#5C6B7A]">Track upcoming and expired memberships. One-click renewal.</p>
      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        {buckets.map(([title, list, c]) => (
          <div key={title} className="rounded-2xl bg-white border border-[#E1E8F0] p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-[#0F2540]">{title}</h3>
              <span className={`text-2xl font-display font-bold ${c}`}>{list.length}</span>
            </div>
            <div className="mt-3 divide-y divide-[#E1E8F0]">
              {list.length === 0 && <div className="text-sm text-[#5C6B7A] py-3">None.</div>}
              {list.map((m) => (
                <div key={m.member_id} className="flex items-center justify-between py-2 text-sm">
                  <div>
                    <div className="font-medium text-[#0F2540]">{m.full_name}</div>
                    <div className="text-xs text-[#5C6B7A]">{m.member_id} · {m.plan_name} · {m.days >= 0 ? `${m.days}d left` : `${Math.abs(m.days)}d ago`}</div>
                  </div>
                  <button onClick={async () => {
                    const memb = (await api.get("/memberships")).data.find((x) => x.member_id === m.member_id);
                    if (memb) extend(memb.membership_id);
                  }} className="text-xs underline text-[#1E4F87]">Renew +1y</button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminCorporate() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [plans, setPlans] = useState([]);
  const [bulk, setBulk] = useState(null); // { lead, plan_id, discount, employees }
  const [filter, setFilter] = useState({ period: "", from_date: "", to_date: "" });
  const load = async () => {
    const [l, p] = await Promise.all([api.get("/corporate-leads", { params: { ...filter, page, limit } }), api.get("/plans?all=true")]);
    setLeads(l.data.items); setTotal(l.data.total); setPlans(p.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, page, limit]);
  useEffect(() => { setPage(1); }, [filter]);

  const updateStatus = async (id, status) => { await api.put(`/corporate-leads/${id}`, { status }); toast.success("Updated"); load(); };

  const onboard = async () => {
    if (!bulk?.employees?.length) return toast.error("Add at least one employee");
    const companyEmail = (bulk.company_email || "").trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(companyEmail)) {
      return toast.error("Company email is required — the consolidated invoice will be sent there.");
    }
    try {
      const r = await api.post("/corporate-leads/bulk-onboard", {
        company_name: bulk.lead.company_name,
        company_email: companyEmail,
        plan_id: bulk.plan_id,
        discount_percent: Number(bulk.discount),
        employees: bulk.employees,
        company_gstin: bulk.company_gstin || "",
        company_state: bulk.company_state || "",
        company_address: bulk.company_address || "",
        consolidated_invoice: !!bulk.consolidated_invoice,
        corporate_order_id: bulk.corporate_order_id || null,
      });
      const skipped = r.data.skipped || [];
      const created = r.data.members || [];
      if (created.length === 0 && skipped.length) {
        // Show the first reason inline so the admin doesn't have to dig.
        const reasons = [...new Set(skipped.map((s) => s.reason).filter(Boolean))].slice(0, 3);
        toast.error(`No members onboarded · ${skipped.length} skipped. ${reasons.join(" · ")}`);
      } else if (skipped.length) {
        toast.error(`Imported ${created.length} · skipped ${skipped.length} (${skipped[0]?.reason || "see logs"})`);
      } else {
        toast.success(`Onboarded ${created.length} employees @ ${inr(r.data.unit_price)} each`);
      }
      if (r.data.consolidated_invoice_no) {
        toast.success(`Consolidated invoice: ${r.data.consolidated_invoice_no}`);
      }
      if (r.data.corporate_email_sent) {
        toast.success(`Corporate invoice emailed to ${companyEmail}`);
      }
      if (bulk.lead.lead_id) {
        await api.put(`/corporate-leads/${bulk.lead.lead_id}`, { status: "Converted" });
      }
      setBulk(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // CSV columns supported (in any order, case-insensitive header):
  //   name (or full_name)  · mobile  · email  · aadhaar  · dob (optional)
  // The header row is REQUIRED so we can map columns by name. Any row missing
  // any of {name, mobile, email, aadhaar} is silently dropped (UI will toast
  // the count). Aadhaar is mandatory because Iter38 #4 enforces 12-digit
  // duplicate-checked Aadhaar for corporate imports.
  const parseCsvText = (text, currentBulk) => {
    const rows = text.replace(/^\uFEFF/, "").trim().split(/\r?\n/);
    if (!rows.length) {
      toast.error("CSV is empty.");
      return;
    }
    const headerCells = rows[0].split(",").map((x) => x.trim().toLowerCase().replace(/[\s_-]/g, ""));
    const headerLooksValid = headerCells.includes("name") || headerCells.includes("fullname")
      || headerCells.includes("mobile") || headerCells.includes("email");
    if (!headerLooksValid) {
      toast.error("First row must be the header: name, mobile, email, aadhaar, dob");
      return;
    }
    const idx = (label) => {
      const aliases = {
        name: ["name", "fullname", "employeename"],
        mobile: ["mobile", "phone", "mobileno", "phoneno"],
        email: ["email", "emailaddress", "emailid"],
        aadhaar: ["aadhaar", "aadhar", "aadharno", "aadhaarno", "aadhaarnumber"],
        dob: ["dob", "dateofbirth", "birthdate"],
      };
      for (const a of aliases[label]) {
        const i = headerCells.indexOf(a);
        if (i !== -1) return i;
      }
      return -1;
    };
    const cols = { name: idx("name"), mobile: idx("mobile"), email: idx("email"),
                   aadhaar: idx("aadhaar"), dob: idx("dob") };
    if (cols.name === -1 || cols.mobile === -1 || cols.email === -1 || cols.aadhaar === -1) {
      toast.error("CSV must include columns: name, mobile, email, aadhaar (dob optional)");
      return;
    }
    let skipped = 0;
    const parsed = rows.slice(1).map((line) => {
      const cells = line.split(",").map((x) => (x || "").trim());
      const full_name = cells[cols.name] || "";
      const mobile = (cells[cols.mobile] || "").replace(/\D/g, "");
      const email = cells[cols.email] || "";
      const aadhaar = (cells[cols.aadhaar] || "").replace(/\D/g, "");
      const dob = cols.dob !== -1 ? (cells[cols.dob] || "") : "";
      if (!full_name || !mobile || !email || !aadhaar) { skipped++; return null; }
      return { full_name, mobile, email, dob, aadhaar };
    }).filter(Boolean);
    if (skipped) toast.message(`${skipped} row(s) skipped (missing required field)`);
    if (!parsed.length) {
      toast.error("No valid rows found. Check that every row has name + mobile + email + 12-digit aadhaar.");
      return;
    }
    setBulk({ ...currentBulk, employees: parsed });
    toast.success(`${parsed.length} employee row(s) ready to onboard`);
  };

  const onCsvFile = (e, currentBulk) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => parseCsvText(String(ev.target?.result || ""), currentBulk);
    reader.readAsText(f);
  };

  // Download a sample CSV so admin always has the correct column header layout.
  const downloadSampleCsv = () => {
    const sample = [
      "name,mobile,email,aadhaar,dob",
      "Rakesh Patel,9876543210,rakesh@example.com,123456789012,1985-04-12",
      "Priya Shah,9876543211,priya@example.com,234567890123,1990-09-30",
    ].join("\n") + "\n";
    const url = URL.createObjectURL(new Blob([sample], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "corporate_members_sample.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-corporate">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold text-[#0F2540]">Corporate</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <DateFilter value={filter} onChange={setFilter} testid="corporate-filter" />
        </div>
      </div>
      <p className="text-[#5C6B7A] mt-1">Two flows: existing leads-based bulk onboarding and pre-paid corporate orders (invoice → payment → onboard).</p>

      <CorporateOrdersPanel plans={plans} onOnboard={(order) => setBulk({
        lead: { lead_id: null, company_name: order.company_name },
        plan_id: order.plan_id,
        discount: order.discount_percent,
        employees: [],
        direct: true,
        corporate_order_id: order.corporate_order_id,
        company_email: order.company_email,
        company_gstin: order.company_gstin,
        company_state: order.company_state,
        company_address: order.company_address,
        consolidated_invoice: true,
      })} />

      <h2 className="mt-10 font-display text-xl font-bold text-[#0F2540]" data-testid="corporate-leads-heading">Leads</h2>
      <div className="mt-3 space-y-3">
        {leads.map((l) => (
          <div key={l.lead_id} className="rounded-2xl bg-white border border-[#E1E8F0] p-5" data-testid={`corp-lead-${l.lead_id}`}>
            <div className="flex justify-between flex-wrap gap-3">
              <div>
                <div className="font-display text-lg font-bold text-[#0F2540]">{l.company_name}</div>
                <div className="text-sm text-[#5C6B7A]">{l.contact_person} · {l.mobile} · {l.email}</div>
                <div className="text-xs text-[#5C6B7A] mt-1">Employees: <b>{l.employee_count}</b> {l.notes && `· ${l.notes}`}</div>
              </div>
              <div className="flex gap-2 items-start">
                <select value={l.status || "New"} onChange={(e) => updateStatus(l.lead_id, e.target.value)}
                  className="px-3 py-1.5 rounded-full text-sm border border-[#E1E8F0] bg-white">
                  {["New", "Contacted", "Proposal Sent", "Converted", "Closed"].map((s) => <option key={s}>{s}</option>)}
                </select>
                <button onClick={() => setBulk({ lead: l, plan_id: plans[0]?.plan_id || "", discount: 10, employees: [], company_email: l.email || "" })}
                  className="text-sm btn-primary !px-4 !py-2" data-testid={`onboard-${l.lead_id}`}>Bulk onboard</button>
              </div>
            </div>
          </div>
        ))}
        {leads.length === 0 && <p className="text-[#5C6B7A]">No corporate leads yet.</p>}
      </div>
      <Pagination page={page} limit={limit} total={total} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} testid="corporate-pagination" />

      {bulk && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-display text-xl font-bold text-[#0F2540]">Bulk onboard · {bulk.lead.company_name}</h3>
            {bulk.corporate_order_id ? (
              <div className="mt-3 rounded-xl border border-[#1F7A3D]/30 bg-[#F0F9F4] p-3 text-xs text-[#0F2540]" data-testid="bulk-corp-order-banner">
                <div className="font-semibold text-[#1F7A3D] mb-1">From corporate order · {bulk.corporate_order_id}</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                  <span>Plan · <b>{plans.find((p) => p.plan_id === bulk.plan_id)?.name}</b></span>
                  <span>Discount · <b>{bulk.discount}%</b></span>
                  <span>Billing email · <b>{bulk.company_email}</b></span>
                  {bulk.company_gstin && <span>GSTIN · <b className="font-mono">{bulk.company_gstin}</b></span>}
                </div>
                <p className="text-[#5C6B7A] mt-2">Just upload the members CSV — all company billing details are pre-filled from the order.</p>
              </div>
            ) : (
              <>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">Plan</label>
                    <select value={bulk.plan_id} onChange={(e) => setBulk({ ...bulk, plan_id: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5" data-testid="bulk-plan-select">
                      {plans.filter((p) => p.is_active).map((p) => <option key={p.plan_id} value={p.plan_id}>{p.name} · {inr(p.price)}</option>)}
                    </select>
                  </div>
                  <F label="Discount %" type="number" v={bulk.discount} s={(v) => setBulk({ ...bulk, discount: v })} />
                </div>
                <div className="mt-3 grid sm:grid-cols-2 gap-3 rounded-xl bg-[#F8FAFC] border border-[#E1E8F0] p-3">
                  <div className="sm:col-span-2">
                    <F label="Company billing email * (consolidated invoice will be sent here)"
                      type="email"
                      v={bulk.company_email || ""}
                      s={(v) => setBulk({ ...bulk, company_email: v })}
                      testid="bulk-company-email" />
                    <p className="text-xs text-[#5C6B7A] mt-1">
                      Only this address will receive the GST invoice. Employees get their welcome card without invoice.
                    </p>
                  </div>
                  <F label="Company GSTIN (B2B invoice)" v={bulk.company_gstin || ""} s={(v) => setBulk({ ...bulk, company_gstin: v.toUpperCase() })} testid="bulk-company-gstin" />
                  <F label="Company state (place of supply)" v={bulk.company_state || ""} s={(v) => setBulk({ ...bulk, company_state: v })} testid="bulk-company-state" />
                  <div className="sm:col-span-2">
                    <label className="text-sm font-medium">Billing address</label>
                    <textarea rows={2} value={bulk.company_address || ""} onChange={(e) => setBulk({ ...bulk, company_address: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5 text-sm" data-testid="bulk-company-address" />
                  </div>
                  <label className="sm:col-span-2 flex items-start gap-2 text-sm text-[#0F2540] cursor-pointer">
                    <input type="checkbox" checked={!!bulk.consolidated_invoice} onChange={(e) => setBulk({ ...bulk, consolidated_invoice: e.target.checked })}
                      className="mt-1 h-4 w-4 accent-[#1E4F87]" data-testid="bulk-consolidated-toggle" />
                    <span>
                      <span className="font-semibold">One consolidated invoice</span> covering all employees in this batch (recommended for corporate B2B). Untick for per-employee invoices.
                    </span>
                  </label>
                </div>
              </>
            )}
            <div className="mt-4">
              <label className="text-sm font-medium">Upload CSV file</label>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => onCsvFile(e, bulk)}
                className="mt-1 w-full text-sm file:btn-ghost file:!px-3 file:!py-1.5 file:!text-xs file:mr-3"
                data-testid="bulk-csv-file"
              />
              <p className="text-[10px] text-[#5C6B7A] mt-1">
                Header row required. Columns (any order): <code>name, mobile, email, aadhaar, dob</code>.
                Aadhaar must be 12 digits and unique. DOB is optional. <button type="button" onClick={downloadSampleCsv} className="text-[#1E4F87] underline" data-testid="dl-sample-csv">Download sample.csv</button>
              </p>
            </div>
            <div className="mt-3">
              <label className="text-sm font-medium">…or paste CSV (name, mobile, email, aadhaar, dob)</label>
              <textarea rows={5} placeholder="Ramesh Kumar,9999911111,ramesh@acme.com,1990-04-12"
                onChange={(e) => parseCsvText(e.target.value, bulk)}
                className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5 font-mono text-sm" data-testid="bulk-csv" />
              <p className="mt-1 text-xs text-[#5C6B7A]">{bulk.employees.length} valid rows parsed.</p>
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <button className="btn-ghost" onClick={() => setBulk(null)}>Cancel</button>
              <button className="btn-primary" onClick={onboard} data-testid="bulk-submit-btn">Onboard {bulk.employees.length} employees</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminEmployees() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(null);
  const load = () => {
    const params = { page, limit };
    if (q.trim()) params.q = q.trim();
    api.get("/employees", { params }).then((r) => { setList(r.data.items); setTotal(r.data.total); });
  };
  // Debounce search-driven reloads so each keystroke doesn't hit the API
  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [page, limit, q]);

  const save = async (e) => {
    // Validate required fields client-side BEFORE hitting the API. Otherwise
    // FastAPI returns a 422 with an array of Pydantic error objects which,
    // when fed to <toast.error>, used to crash the React render tree
    // ("Objects are not valid as a React child") → white screen.
    const name = (e.name || "").trim();
    const mobile = (e.mobile || "").trim();
    const empCode = (e.employee_code || "").trim();
    if (!name) return toast.error("Name is required");
    if (!mobile) return toast.error("Mobile is required");
    if (!/^\d{10}$/.test(mobile)) return toast.error("Mobile must be 10 digits");
    if (!empCode) return toast.error("Employee Code is required");

    const body = {
      name, mobile, pan: e.pan || "", bank_name: e.bank_name || "",
      account_no: e.account_no || "", ifsc: e.ifsc || "", department: e.department || "",
      employee_code: empCode,
      date_of_joining: e.date_of_joining || "",
      referral_code: e.referral_code || "",
    };
    try {
      if (e.employee_id) await api.put(`/employees/${e.employee_id}`, body);
      else await api.post("/employees", body);
      toast.success("Saved"); setEditing(null); load();
    } catch (err) {
      toast.error(apiErr(err, "Save failed"));
    }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/employees/${id}`); load(); };

  const parseCsvAndImport = async () => {
    // Accept comma OR tab separated; header line drives the field mapping.
    // Expected headers (case-insensitive): name, mobile, pan, bank_name, account_no, ifsc, department, employee_code, date_of_joining, referral_code
    const text = (importing?.text || "").trim();
    if (!text) return toast.error("Paste at least one row");
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length < 2) return toast.error("Need a header line + at least one data row");
    const splitRow = (l) => l.includes("\t") ? l.split("\t") : l.split(",").map((x) => x.replace(/^"|"$/g, ""));
    const headers = splitRow(lines[0]).map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
    const required = ["name", "mobile"];
    for (const r of required) if (!headers.includes(r)) return toast.error(`Header "${r}" is required`);
    const rows = lines.slice(1).map((l) => {
      const cells = splitRow(l).map((c) => c.trim());
      const obj = {};
      headers.forEach((h, i) => { obj[h] = cells[i] ?? ""; });
      // Sanitize fields
      if (obj.mobile) obj.mobile = obj.mobile.replace(/\D/g, "").slice(0, 10);
      if (obj.pan) obj.pan = obj.pan.toUpperCase();
      if (obj.ifsc) obj.ifsc = obj.ifsc.toUpperCase();
      return obj;
    });
    try {
      const r = await api.post("/employees/bulk-import", { employees: rows });
      toast.success(`Imported ${r.data.count} · Skipped ${r.data.skipped?.length || 0}`);
      setImporting(null); load();
      if (r.data.skipped?.length) {
        console.warn("Skipped rows:", r.data.skipped);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    }
  };

  const onFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    setImporting({ ...importing, text });
  };

  // CSV export of the entire employee master (#10 Iter38 Phase C)
  const exportEmployees = async () => {
    try {
      const r = await api.get("/employees/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `employees_${ts}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Employee master downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    }
  };

  return (
    <div data-testid="admin-employees">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display text-3xl font-bold text-[#0F2540]">Employee Master</h1>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <input
              type="search"
              value={q}
              onChange={(e) => { setPage(1); setQ(e.target.value); }}
              placeholder="Search by code, name, mobile, dept…"
              className="w-72 max-w-full rounded-xl border border-[#E1E8F0] bg-white pl-9 pr-3 py-2.5 text-sm outline-none focus:border-[#1E4F87]"
              data-testid="emp-search-input"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C6B7A]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
          <button className="btn-ghost !text-sm" onClick={() => setImporting({ text: "" })} data-testid="emp-bulk-import-btn">+ Bulk Import (CSV)</button>
          <button className="btn-ghost !text-sm" onClick={exportEmployees} data-testid="emp-export-btn">⬇ Export CSV</button>
          <button className="btn-primary" onClick={() => setEditing({})} data-testid="new-emp-btn">New employee</button>
        </div>
      </div>
      {editing && (
        <div className="mt-6 rounded-2xl bg-white border border-[#E1E8F0] p-6 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Name *" v={editing.name} s={(v) => setEditing({ ...editing, name: v })} />
            <F label="Mobile *" v={editing.mobile} s={(v) => setEditing({ ...editing, mobile: (v || "").replace(/\D/g, "").slice(0, 10) })} />
            <F label="Employee Code *" v={editing.employee_code} s={(v) => setEditing({ ...editing, employee_code: v })} testid="emp-code-input" />
            <F label="Date of Joining" v={editing.date_of_joining} s={(v) => setEditing({ ...editing, date_of_joining: v })} type="date" testid="emp-doj-input" />
            <F label="Department" v={editing.department} s={(v) => setEditing({ ...editing, department: v })} />
            <F label="Discount/Referral Code (auto-random if blank)" v={editing.referral_code} s={(v) => setEditing({ ...editing, referral_code: v })} testid="emp-ref-code-input" />
            <F label="PAN" v={editing.pan} s={(v) => setEditing({ ...editing, pan: (v || "").toUpperCase() })} />
            <F label="Bank" v={editing.bank_name} s={(v) => setEditing({ ...editing, bank_name: v })} />
            <F label="A/C No." v={editing.account_no} s={(v) => setEditing({ ...editing, account_no: v })} />
            <F label="IFSC" v={editing.ifsc} s={(v) => setEditing({ ...editing, ifsc: (v || "").toUpperCase() })} />
          </div>
          <div className="flex gap-2">
            <button className="btn-primary" onClick={() => save(editing)} data-testid="save-emp-btn">Save</button>
            <button className="btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}
      {importing && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) toast("Click outside disabled — use Cancel/× to close."); }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto space-y-3" data-testid="emp-import-modal">
            <h3 className="font-display text-xl font-bold text-[#0F2540]">Bulk import employees</h3>
            <p className="text-xs text-[#5C6B7A]">CSV header line is required. Recognised columns:
              <code className="text-[#0F2540] mx-1">name, mobile, employee_code, pan, bank_name, account_no, ifsc, department, date_of_joining, referral_code</code>.
              Required: <b>name</b>, <b>mobile</b>, <b>employee_code</b>. Discount code is auto-generated if blank.</p>
            <div>
              <label className="text-sm font-medium">Upload .csv file</label>
              <input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0])} className="mt-1 block w-full text-sm" data-testid="emp-import-file" />
            </div>
            <div>
              <label className="text-sm font-medium">…or paste CSV/TSV content</label>
              <textarea
                rows={8}
                value={importing.text}
                onChange={(e) => setImporting({ ...importing, text: e.target.value })}
                placeholder="name,mobile,employee_code,department,date_of_joining&#10;Asha Sharma,9876511111,HR001,Cardiology,2024-04-01&#10;Vikram Joshi,9876522222,HR002,Front Desk,2024-06-15"
                className="mt-1 w-full rounded-xl border border-[#E1E8F0] px-3 py-2 text-xs font-mono"
                data-testid="emp-import-textarea"
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button className="btn-ghost" onClick={() => setImporting(null)}>Cancel</button>
              <button className="btn-primary" onClick={parseCsvAndImport} data-testid="emp-import-submit">Import</button>
            </div>
          </div>
        </div>
      )}
      <div className="mt-6 rounded-2xl bg-white border border-[#E1E8F0] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EAF1FA] text-[#5C6B7A]">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Emp Code</th>
              <th className="text-left px-4 py-3">Mobile</th>
              <th className="text-left px-4 py-3">DOJ</th>
              <th className="text-left px-4 py-3">Discount Code</th>
              <th className="text-left px-4 py-3">Bank</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((e) => (
              <tr key={e.employee_id} className="border-t border-[#E1E8F0]" data-testid={`emp-row-${e.employee_id}`}>
                <td className="px-4 py-3">{e.name}<div className="text-xs text-[#5C6B7A]">{e.department}</div></td>
                <td className="px-4 py-3 font-mono text-xs text-[#0F2540]">{e.employee_code || "—"}</td>
                <td className="px-4 py-3">{e.mobile}</td>
                <td className="px-4 py-3 text-xs text-[#5C6B7A]">{e.date_of_joining || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#A0741F]">{e.referral_code}</td>
                <td className="px-4 py-3 text-xs text-[#5C6B7A]">{e.bank_name} {e.account_no ? `· ••${e.account_no.slice(-4)}` : ""}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button className="text-xs underline text-[#1E4F87] mr-3" onClick={() => setEditing(e)}>Edit</button>
                  <button className="text-xs underline text-[#E63946]" onClick={() => del(e.employee_id)}>Delete</button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-[#5C6B7A]">{q ? `No employees match "${q}".` : "No employees."}</td></tr>}
          </tbody>
        </table>
        <Pagination page={page} limit={limit} total={total} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} testid="employees-pagination" />
      </div>
    </div>
  );
}

export function AdminReferrals() {
  const [refs, setRefs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sheet, setSheet] = useState([]);
  const [filter, setFilter] = useState({ period: "", from_date: "", to_date: "" });
  const [extras, setExtras] = useState({ user_type: "", code: "", payment_status: "" });
  const [extData, setExtData] = useState({ items: [], total: 0, by_sharer: [] });
  useEffect(() => {
    api.get("/referrals", { params: { ...filter, page, limit } })
      .then((r) => { setRefs(r.data.items); setTotal(r.data.total); });
  }, [filter, page, limit]);
  useEffect(() => { setPage(1); }, [filter, extras]);
  useEffect(() => { api.get("/incentive-sheet").then((r) => setSheet(r.data)); }, []);
  useEffect(() => {
    api.get("/discount-extensions", { params: { ...filter, limit: 100 } })
      .then((r) => setExtData(r.data || { items: [], total: 0, by_sharer: [] }))
      .catch(() => {});
  }, [filter]);

  // Apply client-side filters on top of the server pagination (small dataset; cheap)
  const filteredRefs = useMemo(() => refs.filter((r) => {
    if (extras.user_type && r.user_type !== extras.user_type) return false;
    if (extras.code && !(r.code || "").toLowerCase().includes(extras.code.toLowerCase())) return false;
    if (extras.payment_status && (r.payment_status || "").toLowerCase() !== extras.payment_status.toLowerCase()) return false;
    return true;
  }), [refs, extras]);

  const downloadCSV = () => {
    const header = ["employee_id", "name", "pan", "bank_name", "account_no", "ifsc", "referrals", "sales", "incentive"];
    const rows = sheet.map((s) => header.map((h) => `"${(s[h] ?? "").toString().replace(/"/g, '""')}"`).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `incentive-sheet-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const downloadUtilCSV = () => {
    const header = ["created_at","code","user_type","sharer_name","sharer_id","customer_name","membership_id",
                    "plan_name","amount","discount_amount","incentive","extension_triggered","payment_status"];
    const rows = filteredRefs.map((r) => [
      r.created_at, r.code, r.user_type,
      r.user_type === "Employee" ? r.employee_name : r.sharer_name,
      r.user_type === "Employee" ? r.employee_id : r.sharer_member_id,
      r.customer_name, r.membership_id, r.plan_name, r.amount, r.discount_amount,
      r.incentive, r.extension_triggered ? `+${r.extension_triggered} days` : "",
      r.payment_status,
    ].map((v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `discount-utilisation-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Summary aggregations
  const totalIncentive = useMemo(() => sheet.reduce((s, r) => s + (r.incentive || 0), 0), [sheet]);
  const totalUsage = total;
  const totalDiscountGiven = useMemo(
    () => refs.reduce((s, r) => s + (r.discount_amount || 0), 0),
    [refs]
  );
  const totalExtensionDays = useMemo(
    () => (extData.by_sharer || []).reduce((s, r) => s + (r.total_days_added || 0), 0),
    [extData.by_sharer]
  );
  const totalExtensionsTriggered = useMemo(
    () => (extData.items || []).length,
    [extData.items]
  );
  // Top shared codes — count usage by code
  const topCodes = useMemo(() => {
    const counts = {};
    for (const r of refs) {
      if (!r.code) continue;
      if (!counts[r.code]) counts[r.code] = { code: r.code, count: 0, name: r.user_type === "Employee" ? r.employee_name : r.sharer_name, user_type: r.user_type };
      counts[r.code].count += 1;
    }
    return Object.values(counts).sort((a, b) => b.count - a.count).slice(0, 5);
  }, [refs]);

  return (
    <div data-testid="admin-referrals">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#0F2540]">Discount Code Utilisation</h1>
          <p className="text-[#5C6B7A]">Operational tracking of every discount code applied — who shared it, who used it, what was given, what was triggered.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DateFilter value={filter} onChange={setFilter} testid="referrals-filter" />
          <button className="btn-ghost" onClick={downloadUtilCSV} data-testid="download-utilisation-csv">Utilisation CSV</button>
          <button className="btn-ghost" onClick={downloadCSV} data-testid="download-incentive-csv">Incentive Sheet CSV</button>
        </div>
      </div>

      {/* === Summary cards === */}
      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="discount-summary-cards">
        <SummaryCard label="Total Discount Code Usage" value={totalUsage} sub="codes applied" tone="navy" />
        <SummaryCard label="Total Discounts Given" value={inr(totalDiscountGiven)} sub="to new members" tone="green" />
        <SummaryCard label="Total Extensions Triggered" value={`${totalExtensionsTriggered} (+${totalExtensionDays}d)`} sub="member-side shares" tone="gold" />
        <SummaryCard label="Total Employee Incentives" value={inr(totalIncentive)} sub="all employees, all time" tone="maroon" />
      </div>

      {/* === Top shared codes === */}
      {topCodes.length > 0 && (
        <div className="mt-6">
          <h2 className="font-display text-lg font-bold text-[#0F2540]">Top Shared Codes</h2>
          <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {topCodes.map((c) => (
              <div key={c.code} className="rounded-2xl bg-white border border-[#E1E8F0] p-4">
                <div className="text-xs uppercase tracking-[0.16em] text-[#5C6B7A]">{c.user_type}</div>
                <div className="mt-1 font-mono text-sm font-bold text-[#0F2540]">{c.code}</div>
                <div className="text-xs text-[#5C6B7A] truncate">{c.name || "—"}</div>
                <div className="mt-2 text-sm text-[#A53A2A] font-semibold">{c.count} use{c.count === 1 ? "" : "s"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === Filters === */}
      <div className="mt-6 rounded-2xl bg-white border border-[#E1E8F0] p-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="discount-filters">
        <div>
          <label className="text-xs text-[#5C6B7A]">Shared by</label>
          <select value={extras.user_type} onChange={(e) => setExtras({ ...extras, user_type: e.target.value })}
            className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2 text-sm"
            data-testid="filter-user-type">
            <option value="">All (employee + member)</option>
            <option value="Employee">Employee</option>
            <option value="Member">Member</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-[#5C6B7A]">Code contains</label>
          <input value={extras.code} onChange={(e) => setExtras({ ...extras, code: e.target.value })}
            placeholder="EMP-… / NPC0011…"
            className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2 text-sm"
            data-testid="filter-code" />
        </div>
        <div>
          <label className="text-xs text-[#5C6B7A]">Payment status</label>
          <select value={extras.payment_status} onChange={(e) => setExtras({ ...extras, payment_status: e.target.value })}
            className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2 text-sm"
            data-testid="filter-payment-status">
            <option value="">All</option>
            <option value="success">Paid</option>
            <option value="paid">Paid (offline)</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* === Main utilisation table === */}
      <div className="mt-4 rounded-2xl bg-white border border-[#E1E8F0] overflow-x-auto" data-testid="utilisation-table">
        <table className="w-full text-sm">
          <thead className="bg-[#EAF1FA] text-[#5C6B7A]">
            <tr>
              <th className="text-left px-4 py-3">Discount Code</th>
              <th className="text-left px-4 py-3">Shared By</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Used By</th>
              <th className="text-left px-4 py-3">Membership</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Discount</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Employee Incentive</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Extension</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Registered</th>
              <th className="text-left px-4 py-3">Payment</th>
            </tr>
          </thead>
          <tbody>
            {filteredRefs.map((r) => (
              <tr key={r.referral_id} className="border-t border-[#E1E8F0]">
                <td className="px-4 py-3 font-mono text-xs">{r.code}</td>
                <td className="px-4 py-3">
                  {r.user_type === "Employee" ? (
                    <>{r.employee_name || "—"}<div className="text-xs text-[#5C6B7A] font-mono">{r.employee_id}</div></>
                  ) : (
                    <>{r.sharer_name || "—"}<div className="text-xs text-[#5C6B7A] font-mono">{r.sharer_member_id}</div></>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] px-2 py-1 rounded-full uppercase tracking-wider font-semibold ${
                    r.user_type === "Employee" ? "bg-[#A53A2A]/10 text-[#A53A2A]" : "bg-[#1E4F87]/10 text-[#1E4F87]"
                  }`}>{r.user_type}</span>
                </td>
                <td className="px-4 py-3">{r.customer_name}<div className="text-xs text-[#5C6B7A] font-mono">{r.member_id}</div></td>
                <td className="px-4 py-3 font-mono text-xs text-[#5C6B7A]">{r.membership_id}</td>
                <td className="px-4 py-3 text-[#1F7A3D] font-semibold whitespace-nowrap">{inr(r.discount_amount || 0)}</td>
                <td className="px-4 py-3 text-[#A53A2A] font-semibold whitespace-nowrap">{r.user_type === "Employee" ? inr(r.incentive) : "—"}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {r.extension_triggered > 0
                    ? <span className="text-xs px-2 py-1 rounded-full bg-[#C89B3C]/15 text-[#7A5A1F] font-semibold">+{r.extension_triggered} days</span>
                    : <span className="text-xs text-[#5C6B7A]">—</span>}
                </td>
                <td className="px-4 py-3 text-[#5C6B7A] whitespace-nowrap text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN") : "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    (r.payment_status || "").toLowerCase() === "success" || (r.payment_status || "").toLowerCase() === "paid"
                      ? "bg-[#1F7A3D]/10 text-[#1F7A3D]"
                      : "bg-[#C89B3C]/15 text-[#7A5A1F]"
                  }`}>{r.payment_status || "—"}</span>
                </td>
              </tr>
            ))}
            {filteredRefs.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-[#5C6B7A]">No discount code usage matches your filters.</td></tr>
            )}
          </tbody>
        </table>
        <Pagination page={page} limit={limit} total={total} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} testid="referrals-pagination" />
      </div>

      {/* === Membership extension history (admin only) === */}
      <h2 className="mt-10 font-display text-lg font-bold text-[#0F2540]">Membership Extension History</h2>
      <p className="text-sm text-[#5C6B7A] mb-3">Each time an existing member&#39;s code triggered the silent +15 day extension benefit. Member dashboard does NOT surface this.</p>
      <div className="rounded-2xl bg-white border border-[#E1E8F0] overflow-x-auto" data-testid="extensions-table">
        <table className="w-full text-sm">
          <thead className="bg-[#EAF1FA] text-[#5C6B7A]">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Sharer (got extension)</th>
              <th className="text-left px-4 py-3">User (used code)</th>
              <th className="text-left px-4 py-3">Days Added</th>
              <th className="text-left px-4 py-3">New Valid Till</th>
              <th className="text-left px-4 py-3">Source</th>
            </tr>
          </thead>
          <tbody>
            {(extData.items || []).map((r) => (
              <tr key={r.extension_id} className="border-t border-[#E1E8F0]">
                <td className="px-4 py-3 text-[#5C6B7A] whitespace-nowrap text-xs">{r.created_at ? new Date(r.created_at).toLocaleString("en-IN") : "—"}</td>
                <td className="px-4 py-3">{r.extended_member_name || "—"}<div className="text-xs font-mono text-[#5C6B7A]">{r.extended_member_id}</div></td>
                <td className="px-4 py-3">{r.triggered_by_name || "—"}<div className="text-xs font-mono text-[#5C6B7A]">{r.triggered_by_member_id}</div></td>
                <td className="px-4 py-3 font-semibold text-[#1F7A3D]">+{r.days_added}</td>
                <td className="px-4 py-3 whitespace-nowrap">{r.new_valid_till ? new Date(r.new_valid_till).toLocaleDateString("en-IN") : "—"}</td>
                <td className="px-4 py-3 text-xs text-[#5C6B7A]">{r.source}</td>
              </tr>
            ))}
            {(extData.items || []).length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-[#5C6B7A]">No extensions granted yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sub, tone }) {
  const colors = {
    navy: "bg-gradient-to-br from-[#0F2540] to-[#1E4F87] text-white",
    green: "bg-white border border-[#E1E8F0] text-[#0F2540]",
    gold: "bg-white border border-[#E1E8F0] text-[#0F2540]",
    maroon: "bg-white border border-[#E1E8F0] text-[#0F2540]",
  };
  const valueTone = {
    navy: "text-white",
    green: "text-[#1F7A3D]",
    gold: "text-[#7A5A1F]",
    maroon: "text-[#A53A2A]",
  };
  return (
    <div className={`rounded-2xl p-5 ${colors[tone] || colors.green}`}>
      <div className={`text-[10px] uppercase tracking-[0.18em] ${tone === "navy" ? "opacity-80" : "text-[#5C6B7A]"}`}>{label}</div>
      <div className={`mt-2 font-display text-2xl sm:text-3xl font-bold ${valueTone[tone] || ""}`}>{value}</div>
      <div className={`mt-1 text-xs ${tone === "navy" ? "opacity-70" : "text-[#5C6B7A]"}`}>{sub}</div>
    </div>
  );
}

export function AdminInvoices() {
  const [type, setType] = useState("B2C");
  const [report, setReport] = useState({ totals: {}, invoices: [] });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [filter, setFilter] = useState({ period: "", from_date: "", to_date: "" });
  const load = (t) => api.get(`/reports/gst`, { params: { type: t, ...filter, page, limit } }).then((r) => {
    setReport(r.data);
    setTotal(r.data.total ?? r.data.totals?.count ?? 0);
  });
  useEffect(() => { load(type); /* eslint-disable-next-line */ }, [type, filter, page, limit]);
  useEffect(() => { setPage(1); }, [type, filter]);

  const downloadInvoicePdf = async (inv) => {
    try {
      const safe = inv.invoice_no.replace(/[^A-Za-z0-9._-]/g, "-");
      await downloadFile(`/invoices/by-no/${encodeURIComponent(inv.invoice_no)}/pdf`, `NPC-Invoice-${safe}.pdf`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not download invoice");
    }
  };

  const downloadCSV = () => {
    const header = ["invoice_no", "type", "gst_split_type", "issued_at", "customer_name", "customer_email", "customer_state", "gst_number", "plan_name", "base_price", "discount", "taxable", "cgst", "sgst", "igst", "gst", "gst_percent", "final", "gross"];
    const rows = report.invoices.map((i) => header.map((h) => `"${(i[h] ?? "").toString().replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `gst-${type}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-invoices">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold text-[#0F2540]">GST · Invoices</h1>
        <DateFilter value={filter} onChange={setFilter} testid="invoices-filter" />
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {["B2C", "B2B"].map((t) => (
          <button key={t} onClick={() => setType(t)} className={`px-3 py-1.5 rounded-full text-sm ${type === t ? "bg-[#1E4F87] text-white" : "bg-white border border-[#E1E8F0]"}`} data-testid={`gst-tab-${t}`}>{t}</button>
        ))}
        <button className="btn-primary !px-4 !py-2 ml-auto" onClick={downloadCSV} data-testid="gst-download-csv">Download CSV</button>
        <button
          className="btn-ghost !px-4 !py-2"
          onClick={async () => {
            const f = filter.from_date || ""; const t = filter.to_date || "";
            const fname = `GSTR1_${f || "all"}_${t || "today"}.xlsx`;
            await downloadFile(`/reports/gstr1.xlsx${f || t ? `?from_date=${f}&to_date=${t}` : ""}`, fname);
          }}
          data-testid="gstr1-download-xlsx"
        >Download GSTR-1 (Excel)</button>
      </div>
      <div className="mt-4 grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[["Invoices", report.totals.count || 0], ["Gross", inr(report.totals.gross || 0)], ["Taxable", inr(report.totals.taxable || 0)], ["CGST", inr(report.totals.cgst || 0)], ["SGST", inr(report.totals.sgst || 0)], ["IGST", inr(report.totals.igst || 0)]].map(([l, v]) => (
          <div key={l} className="rounded-2xl bg-white border border-[#E1E8F0] p-4">
            <div className="text-xs uppercase tracking-wider text-[#5C6B7A]">{l}</div>
            <div className="mt-1 font-display text-xl font-bold text-[#0F2540]">{v}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-2xl bg-white border border-[#E1E8F0] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EAF1FA] text-[#5C6B7A]">
            <tr><th className="text-left px-4 py-3">Invoice</th><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Customer</th><th className="text-left px-4 py-3">Plan</th><th className="text-left px-4 py-3">Base</th><th className="text-left px-4 py-3">Disc.</th><th className="text-left px-4 py-3">GST</th><th className="text-left px-4 py-3">Total</th><th className="text-right px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {report.invoices.map((i) => (
              <tr key={i.invoice_no} className="border-t border-[#E1E8F0]">
                <td className="px-4 py-3 font-mono text-xs">{i.invoice_no}</td>
                <td className="px-4 py-3 text-xs text-[#5C6B7A]">{new Date(i.issued_at).toLocaleDateString("en-IN")}</td>
                <td className="px-4 py-3">{i.customer_name}<div className="text-xs text-[#5C6B7A]">{i.customer_email}</div></td>
                <td className="px-4 py-3">{i.plan_name}</td>
                <td className="px-4 py-3">{inr(i.base_price ?? i.taxable)}</td>
                <td className="px-4 py-3 text-[#1F7A3D]">{i.discount > 0 ? `− ${inr(i.discount)}` : "—"}</td>
                <td className="px-4 py-3">{inr(i.gst ?? ((i.cgst || 0) + (i.sgst || 0) + (i.igst || 0)))}</td>
                <td className="px-4 py-3 font-semibold">{inr(i.final ?? i.gross)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => downloadInvoicePdf(i)} className="text-xs text-[#1E4F87] underline" data-testid={`admin-dl-invoice-${i.invoice_no.replace(/\W/g, "_")}`}>Download PDF</button>
                </td>
              </tr>
            ))}
            {report.invoices.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-center text-[#5C6B7A]">No invoices.</td></tr>}
          </tbody>
        </table>
        <Pagination page={page} limit={limit} total={total} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} testid="invoices-pagination" />
      </div>
    </div>
  );
}

export function AdminUsers() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [roles, setRoles] = useState([]);
  const load = () => api.get("/admin-users").then((r) => setList(r.data));
  const loadRoles = () => api.get("/admin/roles").then((r) => setRoles(r.data?.roles || []));
  useEffect(() => { load(); loadRoles(); }, []);

  const save = async (u) => {
    try {
      if (u.user_id) {
        const body = { name: u.name, role: u.role, is_active: u.is_active };
        if (u.password) body.password = u.password;
        if (u.email !== undefined) body.email = u.email || null;
        if (u.username !== undefined) body.username = u.username || null;
        await api.put(`/admin-users/${u.user_id}`, body);
      } else {
        await api.post("/admin-users", {
          email: u.email || null,
          username: u.username || null,
          name: u.name,
          password: u.password,
          role: u.role,
          is_active: u.is_active,
        });
      }
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this admin user?")) return;
    try { await api.delete(`/admin-users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const resetPwd = async (u) => {
    const ident = u.email || u.username || u.name;
    const pwd = window.prompt(`Reset password for ${u.name} (${ident}).\n\nEnter new password (min 8 chars):`);
    if (!pwd) return;
    if (pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      await api.post(`/admin-users/${u.user_id}/reset-password`, { new_password: pwd });
      toast.success(`Password reset for ${ident}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  const BUILTIN_SUFFIX = {
    super_admin: "full control",
    admin: "full app access",
    accounts: "invoices & GST",
    staff: "members & leads",
  };
  const ROLES = roles.length
    ? roles
        .filter((r) => r.is_active !== false)
        .map((r) => [r.name, r.is_builtin ? `${r.display_name} · ${BUILTIN_SUFFIX[r.name] || "built-in"}` : `${r.display_name} · custom`])
    : [
        ["super_admin", "Super Admin · full control"],
        ["admin", "Admin · full app access"],
        ["accounts", "Accounts · invoices & GST"],
        ["staff", "Staff · members & leads"],
      ];

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#0F2540]">User Management</h1>
          <p className="text-[#5C6B7A]">Create and manage admin / accounts / staff accounts. Only Super Admin can edit.</p>
        </div>
        <button className="btn-primary" onClick={() => setEditing({ role: "staff", is_active: true })} data-testid="new-admin-user-btn">New user</button>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) toast("Click outside disabled — use Cancel/× to close."); }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-6 space-y-3">
            <h3 className="font-display text-xl font-bold text-[#0F2540]">{editing.user_id ? "Edit user" : "Create user"}</h3>
            <p className="text-xs text-[#5C6B7A] -mt-1">Provide email, username, or both. At least one is required for login.</p>
            <F label="Email (optional)" v={editing.email || ""} s={(v) => setEditing({ ...editing, email: v })} />
            <F label="Username (optional)" v={editing.username || ""} s={(v) => setEditing({ ...editing, username: v.toLowerCase() })} />
            <F label="Name" v={editing.name} s={(v) => setEditing({ ...editing, name: v })} />
            <div>
              <label className="text-sm font-medium">Role</label>
              <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5" data-testid="admin-user-role">
                {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <F label={editing.user_id ? "New password (leave blank to keep)" : "Password"} type="password" v={editing.password} s={(v) => setEditing({ ...editing, password: v })} />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_active !== false} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active</label>
            <div className="flex gap-2 justify-end pt-2">
              <button className="btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn-primary" onClick={() => save(editing)} data-testid="save-admin-user-btn">Save</button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 rounded-2xl bg-white border border-[#E1E8F0] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EAF1FA] text-[#5C6B7A]">
            <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Username</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.user_id} className="border-t border-[#E1E8F0]">
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3 font-mono text-xs" data-testid={`admin-user-email-${u.user_id}`}>{u.email || <span className="text-[#5C6B7A]">—</span>}</td>
                <td className="px-4 py-3 font-mono text-xs" data-testid={`admin-user-username-${u.user_id}`}>{u.username || <span className="text-[#5C6B7A]">—</span>}</td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-full bg-[#EAF1FA] text-[#1E4F87] font-semibold uppercase tracking-wider">{(u.role || "").replace("_", " ")}</span></td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${u.is_active === false ? "bg-[#E63946]/10 text-[#E63946]" : "bg-[#1F7A3D]/10 text-[#1F7A3D]"}`}>{u.is_active === false ? "Disabled" : "Active"}</span></td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button onClick={() => setEditing({ ...u })} className="text-xs underline text-[#1E4F87] mr-3" data-testid={`edit-admin-user-${u.user_id}`}>Edit</button>
                  <button onClick={() => resetPwd(u)} className="text-xs underline text-[#0F2540] mr-3" data-testid={`reset-admin-pwd-${u.user_id}`}>Reset Pwd</button>
                  <button onClick={() => del(u.user_id)} className="text-xs underline text-[#E63946]" data-testid={`delete-admin-user-${u.user_id}`}>Delete</button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-[#5C6B7A]">No admin users.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// =========================================================
// Roles & Permissions Management (Dynamic RBAC)
// =========================================================
export function AdminRoles() {
  const [data, setData] = useState({ roles: [], available_permissions: [] });
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/roles").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const save = async (r) => {
    try {
      if (r._isNew) {
        await api.post("/admin/roles", { name: r.name, display_name: r.display_name, permissions: r.permissions, is_active: r.is_active });
      } else {
        await api.put(`/admin/roles/${r.name}`, { display_name: r.display_name, permissions: r.permissions, is_active: r.is_active });
      }
      toast.success("Role saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  const del = async (name) => {
    if (!window.confirm(`Delete role "${name}"? Users with this role must be reassigned first.`)) return;
    try { await api.delete(`/admin/roles/${name}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const togglePerm = (perm) => {
    const set = new Set(editing.permissions || []);
    if (set.has(perm)) set.delete(perm); else set.add(perm);
    setEditing({ ...editing, permissions: Array.from(set) });
  };

  return (
    <div data-testid="admin-roles">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#0F2540]">Roles &amp; Permissions</h1>
          <p className="text-[#5C6B7A]">Built-in roles are read-only. Create custom roles and pick which sections they can access.</p>
        </div>
        <button className="btn-primary" onClick={() => setEditing({ _isNew: true, name: "", display_name: "", permissions: [], is_active: true, is_builtin: false })} data-testid="new-role-btn">New role</button>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        {data.roles.map((r) => (
          <div key={r.name} className="rounded-2xl border border-[#E1E8F0] bg-white p-5" data-testid={`role-card-${r.name}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold text-[#0F2540]">{r.display_name}</span>
                  {r.is_builtin && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1E4F87]/10 text-[#1E4F87]">Built-in</span>}
                  {!r.is_active && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E63946]/10 text-[#E63946]">Disabled</span>}
                </div>
                <div className="text-xs font-mono text-[#5C6B7A] mt-1">{r.name}</div>
              </div>
              <div className="flex gap-2">
                {!r.is_locked && <button onClick={() => setEditing({ ...r })} className="text-xs underline text-[#1E4F87]" data-testid={`edit-role-${r.name}`}>Edit</button>}
                {r.can_delete && <button onClick={() => del(r.name)} className="text-xs underline text-[#E63946]" data-testid={`delete-role-${r.name}`}>Delete</button>}
                {r.is_locked && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#5C6B7A]/10 text-[#5C6B7A]" title="Super Admin permissions are locked">Locked</span>}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {(r.permissions || []).map((p) => (
                <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-[#EAF1FA] text-[#1E4F87] font-mono">{p}</span>
              ))}
              {(r.permissions || []).length === 0 && <span className="text-xs text-[#5C6B7A]">No permissions</span>}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) toast("Click outside disabled — use Cancel/× to close."); }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto space-y-3" data-testid="role-edit-modal">
            <h3 className="font-display text-xl font-bold text-[#0F2540]">{editing._isNew ? "Create custom role" : `Edit role: ${editing.display_name}`}</h3>
            {editing._isNew && (
              <F label="Role key (snake_case, e.g. branch_manager)" v={editing.name}
                 s={(v) => setEditing({ ...editing, name: v.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} />
            )}
            <F label="Display name" v={editing.display_name} s={(v) => setEditing({ ...editing, display_name: v })} />
            <div>
              <label className="text-sm font-medium">Permissions</label>
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                {(data.available_permissions || []).map((p) => (
                  <label key={p} className="flex items-center gap-2 text-xs cursor-pointer rounded-lg px-2 py-1 hover:bg-[#EAF1FA]">
                    <input type="checkbox" checked={(editing.permissions || []).includes(p)} onChange={() => togglePerm(p)} data-testid={`perm-${p}`} />
                    <span className="font-mono">{p}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editing.is_active !== false} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active
            </label>
            <div className="flex gap-2 justify-end pt-2">
              <button className="btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn-primary" onClick={() => save(editing)} data-testid="save-role-btn">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// =========================================================
// Employee Performance Report (Section 16)
// =========================================================
export function AdminEmployeePerformance() {
  const [report, setReport] = useState({ employees: [], totals: {} });
  const [filter, setFilter] = useState({ period: "month", from_date: "", to_date: "" });
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState("");

  useEffect(() => { api.get("/employees").then((r) => setEmployees(r.data)); }, []);

  const load = () => {
    const params = { ...filter };
    if (selected) params.employee_id = selected;
    api.get("/reports/employee-performance", { params }).then((r) => setReport(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, selected]);

  const downloadCSV = () => {
    const header = ["employee_id", "name", "referral_code", "mobile", "total_referrals", "total_registrations", "total_earnings"];
    const rows = (report.employees || []).map((e) => header.map((h) => `"${(e[h] ?? "").toString().replace(/"/g, '""')}"`).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `employee-performance-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = report.totals || {};
  return (
    <div data-testid="admin-employee-performance">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#0F2540]">Employee Performance Report</h1>
          <p className="text-[#5C6B7A]">Discount codes shared, registrations and earnings per employee — filter by date or person.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="text-xs px-3 py-1.5 rounded-full border border-[#E1E8F0] bg-white"
            data-testid="epr-employee-select"
          >
            <option value="">All employees</option>
            {employees.map((e) => (
              <option key={e.employee_id} value={e.employee_id}>
                {e.name} ({e.referral_code})
              </option>
            ))}
          </select>
          <DateFilter value={filter} onChange={setFilter} testid="epr-filter" />
          <button className="btn-primary !px-4 !py-2" onClick={downloadCSV} data-testid="epr-download-csv">Download CSV</button>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        {[
          ["Employees", (report.employees || []).length],
          ["Total Codes Used", totals.referrals || 0],
          ["Total Earnings", inr(totals.earnings || 0)],
        ].map(([l, v]) => (
          <div key={l} className="rounded-2xl bg-white border border-[#E1E8F0] p-4">
            <div className="text-xs uppercase tracking-wider text-[#5C6B7A]">{l}</div>
            <div className="mt-1 font-display text-xl font-bold text-[#0F2540]">{v}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl bg-white border border-[#E1E8F0] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EAF1FA] text-[#5C6B7A]">
            <tr>
              <th className="text-left px-4 py-3">Employee</th>
              <th className="text-left px-4 py-3">Code</th>
              <th className="text-left px-4 py-3">Mobile</th>
              <th className="text-left px-4 py-3">Codes Used</th>
              <th className="text-left px-4 py-3">Registrations</th>
              <th className="text-left px-4 py-3">Earnings</th>
            </tr>
          </thead>
          <tbody>
            {(report.employees || []).map((e) => (
              <tr key={e.employee_id} className="border-t border-[#E1E8F0]" data-testid={`epr-row-${e.employee_id}`}>
                <td className="px-4 py-3">{e.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{e.referral_code}</td>
                <td className="px-4 py-3">{e.mobile || "—"}</td>
                <td className="px-4 py-3">{e.total_referrals}</td>
                <td className="px-4 py-3">{e.total_registrations}</td>
                <td className="px-4 py-3 text-[#1F7A3D] font-semibold">{inr(e.total_earnings)}</td>
              </tr>
            ))}
            {(report.employees || []).length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-[#5C6B7A]">No referral activity in this range.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// =========================================================
// Corporate Orders panel — pre-paid B2B flow (invoice → payment → onboard)
// =========================================================
function CorporateOrdersPanel({ plans, onOnboard }) {
  const [orders, setOrders] = useState([]);
  const [creating, setCreating] = useState(null);
  const [recording, setRecording] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/corporate-orders"); setOrders(Array.isArray(r.data) ? r.data : r.data.items || []); }
    catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const createOrder = async () => {
    if (!creating?.company_name || !creating?.company_email || !creating?.plan_id || !creating?.seats) {
      return toast.error("Company name, email, plan and seats are required.");
    }
    // Mandatory address fields (#6)
    if (!(creating.company_address || "").trim()) return toast.error("Company address is required.");
    if (!(creating.company_city || "").trim()) return toast.error("Company city is required.");
    if (!/^\d{6}$/.test(creating.company_pincode || "")) return toast.error("Pincode must be exactly 6 digits.");
    if (!creating.company_state) return toast.error("Please select the company state.");
    // GSTIN is optional; if supplied, must match format AND state must match GSTIN code
    const gstin = (creating.company_gstin || "").trim().toUpperCase();
    if (gstin) {
      if (gstin.length !== 15) return toast.error("GSTIN must be exactly 15 characters.");
      if (!GSTIN_REGEX.test(gstin)) return toast.error("GSTIN format is invalid.");
      const gstinState = stateFromGSTIN(gstin);
      if (gstinState && gstinState !== creating.company_state) {
        return toast.error(`GSTIN belongs to ${gstinState} — please update the state.`);
      }
    }
    setBusy(true);
    try {
      await api.post("/corporate-orders", {
        company_name: creating.company_name.trim(),
        company_email: creating.company_email.trim(),
        plan_id: creating.plan_id,
        seats: parseInt(creating.seats, 10),
        discount_percent: Number(creating.discount_percent || 0),
        company_gstin: gstin || "",
        company_state: creating.company_state || "",
        company_city: creating.company_city || "",
        company_pincode: creating.company_pincode || "",
        company_address: creating.company_address || "",
        notes: creating.notes || "",
      });
      toast.success("Corporate order created — share invoice with client.");
      setCreating(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const recordPayment = async () => {
    if (!recording?.amount || Number(recording.amount) <= 0) return toast.error("Amount required.");
    setBusy(true);
    try {
      await api.post(`/corporate-orders/${recording.order.corporate_order_id}/payment`, {
        amount: Number(recording.amount),
        method: recording.method || "bank_transfer",
        reference: recording.reference || "",
        notes: recording.notes || "",
      });
      toast.success("Payment recorded");
      setRecording(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this corporate order?")) return;
    try { await api.delete(`/corporate-orders/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const statusPill = (s) => {
    const m = { pending: "bg-[#C89B3C]/15 text-[#A0741F]", partial: "bg-[#1E4F87]/10 text-[#1E4F87]", paid: "bg-[#1F7A3D]/10 text-[#1F7A3D]" };
    return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${m[s] || "bg-[#5C6B7A]/10 text-[#5C6B7A]"}`}>{s}</span>;
  };

  return (
    <section className="mt-6" data-testid="corporate-orders-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-xl font-bold text-[#0F2540]">Pre-Paid Corporate Orders</h2>
        <button className="btn-primary !px-4 !py-2 !text-sm"
          onClick={() => setCreating({ plan_id: plans?.[0]?.plan_id || "", seats: 1, discount_percent: 0 })}
          data-testid="new-corporate-order-btn">
          + New Corporate Order
        </button>
      </div>
      <p className="text-xs text-[#5C6B7A] mt-1">Step 1: Create order &amp; share invoice · Step 2: Company pays (bank transfer) · Step 3: Record payment · Step 4: Onboard members.</p>

      <div className="mt-4 grid gap-3">
        {orders.map((o) => {
          const due = Math.max(0, Number(o.total_amount || 0) - Number(o.paid_amount || 0));
          const canOnboard = (o.payment_status === "paid" || o.payment_status === "partial") && (o.seats_onboarded || 0) < (o.seats || 0);
          return (
            <div key={o.corporate_order_id} className="rounded-2xl bg-white border border-[#E1E8F0] p-5" data-testid={`corp-order-${o.corporate_order_id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display text-lg font-bold text-[#0F2540]">{o.company_name}</span>
                    {statusPill(o.payment_status)}
                    {o.status === "closed" && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#5C6B7A]/10 text-[#5C6B7A]">Closed</span>}
                  </div>
                  <div className="text-sm text-[#5C6B7A]">{o.company_email} · {o.plan_name} · {o.seats} seats ({o.seats_onboarded || 0} onboarded)</div>
                  <div className="text-xs text-[#5C6B7A] mt-1 font-mono">{o.corporate_order_id}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-[#5C6B7A]">Total · <b className="text-[#0F2540]">{inr(o.total_amount)}</b></div>
                  {/* Pre-paid GST breakdown (#9 Iter38 Phase C). Plan prices are
                       GST-inclusive — taxable / GST shown for the buyer's records. */}
                  {(o.gst_amount || o.taxable_amount) ? (
                    <div className="text-[10px] text-[#5C6B7A] mt-0.5 leading-tight" data-testid={`corp-gst-${o.corporate_order_id}`}>
                      Taxable {inr(o.taxable_amount)} · {o.gst_split_type === "igst"
                        ? `IGST ${inr(o.igst)}`
                        : `CGST ${inr(o.cgst)} + SGST ${inr(o.sgst)}`} ({o.gst_percent || 18}%)
                    </div>
                  ) : null}
                  <div className="text-xs text-[#1F7A3D] mt-1">Paid {inr(o.paid_amount)}</div>
                  {due > 0 && <div className="text-xs text-[#A53A2A]">Due {inr(due)}</div>}
                </div>
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                {o.payment_status !== "paid" && (
                  <button className="btn-ghost !text-xs" onClick={() => setRecording({ order: o, amount: due, method: "bank_transfer" })} data-testid={`record-payment-${o.corporate_order_id}`}>Record Payment</button>
                )}
                {canOnboard && (
                  <button className="btn-primary !text-xs" onClick={() => onOnboard(o)} data-testid={`onboard-order-${o.corporate_order_id}`}>Onboard Members</button>
                )}
                {(o.seats_onboarded || 0) === 0 && (
                  <button className="text-xs underline text-[#E63946] ml-auto" onClick={() => del(o.corporate_order_id)}>Delete</button>
                )}
              </div>
            </div>
          );
        })}
        {orders.length === 0 && <p className="text-sm text-[#5C6B7A]">No corporate orders yet. Click "+ New Corporate Order" to start the pre-paid flow.</p>}
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/55 grid place-items-center p-3 sm:p-6" onClick={(e) => { if (e.target === e.currentTarget) toast("Click the × to close — your data is preserved."); }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-[960px] max-h-[94vh] flex flex-col shadow-2xl" data-testid="create-corp-order-modal">
            {/* Sticky header */}
            <div className="px-7 py-4 border-b border-[#E1E8F0] flex items-center justify-between rounded-t-2xl">
              <div>
                <h3 className="font-display text-2xl font-bold text-[#0F2540]">New corporate order</h3>
                <p className="text-xs text-[#5C6B7A] mt-0.5">Pre-paid B2B onboarding · GST invoice generated on creation.</p>
              </div>
              <button onClick={() => !busy && setCreating(null)} className="text-[#5C6B7A] hover:text-[#E63946] text-2xl leading-none px-1" aria-label="Close" data-testid="corp-close">×</button>
            </div>

            {/* Scrollable body */}
            <div className="px-7 py-6 overflow-y-auto flex-1 space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-[#1E4F87]">Company</h4>
                  <span className="flex-1 h-px bg-[#E1E8F0]" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <F label="Company name *" v={creating.company_name} s={(v) => setCreating({ ...creating, company_name: v })} />
                  <F label="Company email *" v={creating.company_email} s={(v) => setCreating({ ...creating, company_email: v })} />
                  <F label="GSTIN (optional · 15 chars)" v={creating.company_gstin || ""}
                     s={(v) => {
                       const next = (v || "").toUpperCase().replace(/\s/g, "").slice(0, 15);
                       const auto = stateFromGSTIN(next);
                       setCreating({
                         ...creating,
                         company_gstin: next,
                         company_state: auto && !creating.company_state ? auto : creating.company_state,
                       });
                     }}
                     err={creating.company_gstin && creating.company_gstin.length > 0 && (creating.company_gstin.length !== 15 || !GSTIN_REGEX.test(creating.company_gstin))
                       ? "Invalid GSTIN format" : undefined}
                     testid="corp-company-gstin" />
                  <F label="Notes" v={creating.notes} s={(v) => setCreating({ ...creating, notes: v })} />
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-[#1E4F87]">Plan &amp; seats</h4>
                  <span className="flex-1 h-px bg-[#E1E8F0]" />
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium">Plan *</label>
                    <select value={creating.plan_id} onChange={(e) => setCreating({ ...creating, plan_id: e.target.value })} className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5">
                      {(plans || []).filter((p) => p.is_active).map((p) => <option key={p.plan_id} value={p.plan_id}>{p.name} · {inr(p.price)}</option>)}
                    </select>
                  </div>
                  <F label="Seats *" v={creating.seats} s={(v) => setCreating({ ...creating, seats: v })} type="number" />
                  <F label="Discount %" v={creating.discount_percent} s={(v) => setCreating({ ...creating, discount_percent: v })} type="number" />
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-[#1E4F87]">Address (for GST invoice)</h4>
                  <span className="flex-1 h-px bg-[#E1E8F0]" />
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-3"><F label="Address" required v={creating.company_address} s={(v) => setCreating({ ...creating, company_address: v })} testid="corp-company-address" /></div>
                  <F label="City" required v={creating.company_city} s={(v) => setCreating({ ...creating, company_city: v })} testid="corp-company-city" />
                  <F label="Pincode" required v={creating.company_pincode} s={(v) => setCreating({ ...creating, company_pincode: v.replace(/\D/g, "").slice(0, 6) })} testid="corp-company-pincode" />
                  <StateSelect value={creating.company_state} onChange={(v) => setCreating({ ...creating, company_state: v })} testid="corp-company-state" label="State (for IGST split) *" required />
                </div>
              </div>
            </div>

            {/* Sticky footer */}
            <div className="px-7 py-4 border-t border-[#E1E8F0] bg-white rounded-b-2xl flex items-center justify-end gap-2">
              <button className="btn-ghost" onClick={() => setCreating(null)} disabled={busy}>Cancel</button>
              <button className="btn-primary" onClick={createOrder} disabled={busy} data-testid="create-corp-order-submit">{busy ? "Creating…" : "Create order"}</button>
            </div>
          </div>
        </div>
      )}

      {recording && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) toast("Click outside disabled — use Cancel/× to close."); }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-6 space-y-3" data-testid="record-payment-modal">
            <h3 className="font-display text-xl font-bold text-[#0F2540]">Record payment</h3>
            <p className="text-xs text-[#5C6B7A]">{recording.order.company_name} · Outstanding {inr(Math.max(0, Number(recording.order.total_amount || 0) - Number(recording.order.paid_amount || 0)))}</p>
            <F label="Amount *" v={recording.amount} s={(v) => setRecording({ ...recording, amount: v })} type="number" />
            <div>
              <label className="text-sm font-medium">Method</label>
              <select value={recording.method} onChange={(e) => setRecording({ ...recording, method: e.target.value })} className="mt-1 w-full rounded-xl border border-[#E1E8F0] px-3 py-2.5">
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
                <option value="upi">UPI</option>
                <option value="cash">Cash</option>
              </select>
            </div>
            <F label="Reference / Txn ID" v={recording.reference} s={(v) => setRecording({ ...recording, reference: v })} />
            <F label="Notes" v={recording.notes} s={(v) => setRecording({ ...recording, notes: v })} />
            <div className="flex gap-2 justify-end pt-2">
              <button className="btn-ghost" onClick={() => setRecording(null)} disabled={busy}>Cancel</button>
              <button className="btn-primary" onClick={recordPayment} disabled={busy} data-testid="record-payment-submit">Save</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
